<script>
import { preventDefault } from "svelte/legacy";

/* 
    Based on: https://itectec.com/superuser/ffmpeg-merge-two-audio-file-with-defined-overlapping-time/
    Generate Voice Over:
    C:\Users\natha\Documents\Dev\cc-creator-8\node_modules\ffmpeg-static-electron\bin\win\x64\ffmpeg.exe -y -i C:\Users\natha\Videos\fcf0ef1c-c17e-4cf9-b9c9-73a3e5f2deff.mp3 -i C:\Users\natha\Videos\6f0f6133-c5d1-49a1-b992-f26e2dbf35ad.mp3 -filter_complex "[0]adelay=delays=5s:all=1[a1];[1]adelay=delays=2s:all=1[a2];[a1][a2]amix=inputs=2[dv];[dv]speechnorm[master]" -map "[master]" C:\Users\natha\Videos\output.mp3

    C:\Users\natha\Documents\Dev\cc-creator-8\node_modules\ffmpeg-static-electron\bin\win\x64\ffmpeg.exe -y -i C:\Users\natha\Videos\trailer.mp4 -i C:\Users\natha\Videos\fcf0ef1c-c17e-4cf9-b9c9-73a3e5f2deff.mp3 -i C:\Users\natha\Videos\6f0f6133-c5d1-49a1-b992-f26e2dbf35ad.mp3 -filter_complex "[1]adelay=10s|0[a1];[2]adelay=6s|0[a2];[0:a][a1][a2]amix=inputs=3[a]" -map "[a]"  C:\Users\natha\Videos\output.mp3

    HOW TO MEASURE LOUDNESS:
    ------------------------
    ffmpeg.exe -nostats -i "C:\Users\natha\Videos\DV\AD Testing 07-04_MIXDOWN.wav" -filter_complex ebur128 -f null -
*/

/**
 * Audio Description Export Component
 *
 * This component handles the export of audio descriptions, including:
 * - Text-to-speech rendering for individual events
 * - Combining multiple audio files with proper timing
 * - Audio mixdown with program audio
 * - SRT subtitle generation
 * - BWF metadata embedding for WAV files
 */

// Import store dependencies
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { fade } from "svelte/transition";
import { toast } from "@zerodevx/svelte-toast";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { playerState } from "@app/store/playerStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { activateSubscription } from "@app/lib/activateSubscription.js";
/* Firebase */
import { auth, functions } from "@app/configs/firebase.js";
import { httpsCallable } from "firebase/functions";
import { Timestamp } from "firebase/firestore";

/* CC LIB */
import _EventGroup from "@cclib/classes/eventGroup.js";
import encode from "@cclib/functions/encode.js";
import defaults from "@cclib/lib/defaults.js";
import autoFormat from "@cclib/functions/eventGroups/autoFormat.js";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import tcLib from "@cclib/lib/timecode.js";
import voiceUtils from "@cclib/providers/textToSpeechAll/voiceUtils.js";
import { AUDIO_DESCRIPTION, AUDIO_DUBBING } from "@app/lib/eventGroupTypes.js";
import { eventNeedsRender } from "@app/lib/audio/audioRenderState.js";
import { assignEventAudio } from "@app/lib/audio/assignEventAudio.js";
import {
    refreshEventAudioUrl,
    getEventAudioStoragePath,
} from "@app/lib/audio/refreshEventAudioUrl.js";
import orderByTime from "@cclib/functions/eventGroups/orderByTime.js";
import removeHtmlEntities from "@cclib/functions/eventGroups/removeHtmlEntities.js";

import { v4 as uuidv4 } from "uuid";
import { getSelectedEventIdRange } from "@app/lib/eventSelection.js";

// Import presets from lib directory
import loudnessPresets from "@app/lib/presets/loudnessPresets.js";
import audioExportProfiles from "@app/lib/presets/audioExportProfiles.js";
import autoMixPresets from "@app/lib/presets/autoMixPresets.js";
import controlTrackLayouts from "@app/lib/presets/controlTrackLayouts.js";
import { createControlTrackRenderer } from "@app/lib/audio/whp198/controlTrack.js";
import { Whp198Decoder } from "@app/lib/audio/whp198/decoder.js";
import { gainToDb } from "@app/lib/audio/whp198/descriptor.js";
import { DEFAULT_SAMPLE_RATE as CONTROL_TRACK_SAMPLE_RATE } from "@app/lib/audio/whp198/constants.js";
import * as m from "@app/paraglide/messages.js";

/* Preset Svelte Store */
import {
    createPreset,
    getPresetsByType,
    getPresetById,
    PRESET_TYPES
} from "@app/store/presetStore.svelte.js";

/* Electron/FFMPEG */
const ffmpeg = window.ffmpeg;
const { shell, webUtils } = window;
const ipcRenderer = window.ipcRenderer;
const fs = window.fs;
const fsSync = window.fsSync;
const path = window.path;
const exec = window.exec;

import { WaveFile } from "wavefile";

/** @type {{ variant?: "description" | "dubbing" }} */
let { variant = "description" } = $props();
// svelte-ignore state_referenced_locally
const isDubbing = variant === "dubbing";
const modalKey = isDubbing ? "audioDubbingExport" : "audioDescriptionExport";
const settingsStorageKey = isDubbing ? "cc-dub-export" : "cc-ad-export";
const presetType = isDubbing ? PRESET_TYPES.AUDIO_DUBBING : PRESET_TYPES.AUDIO_DESCRIPTION;
const groupType = isDubbing ? AUDIO_DUBBING : AUDIO_DESCRIPTION;

// Show trial warning if user is in trial mode
if (authState.status === "in_trial") {
    showTrialWarning();
}

const adSelectedRange = getSelectedEventIdRange(
    eventGroupState[projectState.selected].selected,
    eventGroupState[projectState.selected].events.length,
    { rippleIfSingle: false },
);

// Load previous export settings from localStorage or use defaults
let adExportDefaults = JSON.parse(localStorage.getItem(settingsStorageKey)) || {},
    exporting = $state(false),
    targetFolderPath = $state(sessionStorage.getItem("audioExportPath")),
    selection = $state(false),
    selectionStart = $state(adSelectedRange.start),
    selectionEnd = $state(adSelectedRange.end),
    // Process configuration
    renderQueueSize = 10, // Number of events to render simultaneously
    stitchQueueSize = 150, // Number of audio files to stitch in one pass
    loops = 0, // Counter for tracking stitch operations
    progress = $state(0), // Export progress percentage
    count = 0, // Counter for current event being processed
    eventQueue, // Queue of events for audio rendering
    retryQueue = [], // Queue for retry attempts
    retryFlag = false, // Flag to indicate if in retry mode
    totalEvents, // Total number of events to process
    voAudioFiles = [], // Array to store paths to rendered audio files
    statusMsg = $state(""), // Status message for UI
    ignoreAll = false, // Flag to ignore all warnings
    copyOfEventGroup, // Working copy of the event group
    eventGroupId = $state(),
    // Loudness settings
    loudnessPreset = $state(),
    loudnessVo = $state(adExportDefaults.loudnessVo),
    loudnessProgram = $state(adExportDefaults.loudnessProgram),
    loudnessMixdown = $state(adExportDefaults.loudnessMixdown),
    // Export options
    keepVoTracks = $state(adExportDefaults.keepVoTracks ?? true),
    exportMixdown = $state(adExportDefaults.exportMixdown ?? true),
    exportSrtFile = $state(adExportDefaults.exportSrtFile ?? false),
    maxLines = $state(2),
    maxChars = $state(42),
    audioPreset = $state(),
    duration = false, // Media duration in seconds
    // Accordion section visibility
    showOutputOptions = $state(true),
    showAudioProcessing = $state(false),
    showAdvancedOptions = $state(false),
    showMetadata = $state(false),
    // Preset-related state
    availablePresets = $derived(getPresetsByType(presetType)),
    // File paths
    voAudioPath,
    voAudioPathB,
    voAudioPathFinal,
    srtFileBasePath,
    mixdownAudioPath,
    eventGroupIndex,
    extension,
    // User and team info
    teamId = authState.team ? authState.team.id : null,
    teamOwner = authState.team ? authState.team.owner : null,
    teamOwnerId = authState.team ? authState.team.ownerId : null,
    userId = auth.currentUser.uid,
    userEmail = auth.currentUser.email,
    // Job tracking information
    jobInfo = {
        id: uuidv4(),
        teamId: teamId,
        owner: teamOwner,
        ownerId: teamOwnerId,
        projectId: projectState.id,
        projectName: projectState.name,
        submittedBy: userEmail,
        userId: userId,
        progress: 100,
        chars: 0, //characters
        cost: 0,
        type: "text to speech",
        statusMsg: "Job Completed",
        status: "Passed",
        createdOn: null,
        updatedOn: null,
        completedOn: null,
        deleted: false,
        config: {
            preset: false,
            loudness: false,
            format: false,
        },
    };

let exportExtendedAD = $state(!isDubbing && (adExportDefaults.exportExtendedAD ?? false));
let extendedADEvents = []; // Store events with adjusted timings
let totalADDuration = 0; // Total duration added by AD events

// AD control track (WHP 198) export
const controlTrackLayoutLabels = {
    "none": () => m.audio_description_export_control_track_none(),
    "ad-control-2ch": () => m.audio_description_export_control_track_2ch(),
};
let controlTrackLayout = $state(
    !isDubbing && controlTrackLayouts.some((layout) => layout.id === adExportDefaults.controlTrackLayout)
        ? adExportDefaults.controlTrackLayout
        : "none",
);
let controlTrackFadeDb = $state(adExportDefaults.controlTrackFadeDb ?? defaultControlTrackFadeDb());
let controlTrackFadeInMs = $state(adExportDefaults.controlTrackFadeInMs ?? 200);
let controlTrackFadeOutMs = $state(adExportDefaults.controlTrackFadeOutMs ?? 200);
let controlTrackPath; // Path for the control track output file

// Fade depth for the control track: follow the saved fixed-gain AutoMix
// reduction when there is one, so the delivered fade matches the mixdown.
function defaultControlTrackFadeDb() {
    const fixedGain = autoMixPresets.find(
        (preset) => preset.displayName === adExportDefaults.autoMixPresetName && preset.type === "fixedGain",
    );
    const db = gainToDb(adExportDefaults.gainReduction ?? fixedGain?.gainReduction);
    return Number.isFinite(db) ? Math.round(db * 10) / 10 : -12;
}

// BWF metadata for WAV exports
let bwfMetadata = $state({
    enable: false,
    title: projectState.name || "Untitled",
    description: projectState.description || " ",
    referenceTime: tcLib.secToTc(
        projectState.incode,
        projectState.frameRate,
        projectState.dropFrame,
    ),
});

// Custom program audio handling
let files = $state([]);
const backgroundStemPath = projectState.media?.stems?.background?.localPath || null;
let customAudioPath = $state(isDubbing ? backgroundStemPath : null); // Path for custom audio file

// Video export handling
let exportVideo = $state(adExportDefaults.exportVideo ?? false);
let videoFiles = $state([]);
const projectVideoPath =
    projectState.media?.localPath && !projectState.media?.type?.startsWith("audio")
        ? projectState.media.localPath
        : null;
let customVideoPath = $state(isDubbing ? projectVideoPath : null); // Path for custom video file
let videoOutputPath; // Path for video output file

// Load preset from saved settings if available
let autoMixPresetIndex = autoMixPresets.findIndex((preset) => {
    return preset.displayName === adExportDefaults.autoMixPresetName;
});

// First get the actual preset object from the array to maintain reference equality
let autoMixPreset = $state(
    autoMixPresetIndex > -1
        ? autoMixPresets[autoMixPresetIndex]
        : isDubbing
          ? autoMixPresets.find((preset) => preset.type === "none")
          : (autoMixPresets.find((preset) => preset.displayName === "Default") ?? autoMixPresets[0]),
);

// Then, if it's the Custom preset, update its properties separately
// svelte-ignore state_referenced_locally
if (autoMixPreset && autoMixPreset.displayName === "Custom") {
    // Set the type to compression if not defined (for backward compatibility)
    if (!autoMixPreset.type) {
        autoMixPreset.type = "compression";
    }
    
    // Update compression-specific properties
    if (autoMixPreset.type === "compression") {
        autoMixPreset.ratio = adExportDefaults.ratio || autoMixPreset.ratio;
        autoMixPreset.threshold = adExportDefaults.threshold || autoMixPreset.threshold;
        autoMixPreset.release = adExportDefaults.release || autoMixPreset.release;
        autoMixPreset.attack = adExportDefaults.attack || autoMixPreset.attack;
        autoMixPreset.knee = adExportDefaults.knee || autoMixPreset.knee;
        autoMixPreset.mix = adExportDefaults.mix || autoMixPreset.mix;
    }
    // Update fixed gain properties if needed
    else if (autoMixPreset.type === "fixedGain") {
        autoMixPreset.gainReduction = adExportDefaults.gainReduction || autoMixPreset.gainReduction;
        autoMixPreset.fadeTime = adExportDefaults.fadeTime || autoMixPreset.fadeTime;
    }
}

let thresholdDb = $derived.by(() => {
    return (20 * Math.log10(autoMixPreset.threshold)).toFixed(1);
});

let loudnessEnabled = $derived(loudnessPreset?.target != null);

// Initialize loudness preset from saved settings
if (adExportDefaults.loudnessPreset) {
    loudnessPreset = loudnessPresets.find((preset) => {
        return (
            preset.displayName === adExportDefaults.loudnessPreset.displayName
        );
    });
}
// svelte-ignore state_referenced_locally
if (!loudnessPreset) {
    loudnessPreset = loudnessPresets.find((preset) =>
        isDubbing ? preset.target == null : preset.target != null,
    );
}

// Ensure all presets have a type property (for backward compatibility)
autoMixPresets.forEach(preset => {
    if (!preset.type) {
        preset.type = "compression";
    }
});

// Initialize audio export preset from saved settings
if (adExportDefaults.audioPreset && adExportDefaults.audioPreset.displayName) {
    audioPreset = audioExportProfiles.find((preset) => {
        return preset.displayName === adExportDefaults.audioPreset.displayName;
    });
}

// Default to the selected Event Group when it matches this export's type, otherwise the first one that does
eventGroupId =
    (eventGroupState[projectState.selected]?.type === groupType
        ? eventGroupState[projectState.selected]
        : eventGroupState.find((group) => group.type === groupType)
    )?.id;

/* Audio Description Export Functions: */
/* 
    1. Begin the export and set all of the global variables for each function
    2. Get the duration of the media (we need this to know how long to make the VO track)
    3. Render each event's audio again
    4. Export VO track
    5. Export mixdown file
    6. Done!

*/

// Function to save current settings as a preset
async function saveAsPreset() {
    const { value: presetName } = await Swal.fire({
        ...SWAL_BASE,
        titleText: m.audio_description_export_preset_save_title(),
        input: "text",
        inputLabel: m.audio_description_export_preset_name_label(),
        inputPlaceholder: m.audio_description_export_preset_name_placeholder(),
        showCancelButton: true,
        confirmButtonText: m.audio_description_export_save_button(),
        cancelButtonText: m.audio_description_export_cancel_button(),
        customClass: SWAL_CLASSES.confirmCancel,
        inputValidator: (value) => {
            if (!value) {
                return m.audio_description_export_preset_name_required();
            }
        }
    });

    if (presetName) {
        try {
            const config = {
                loudnessVo,
                loudnessProgram,
                loudnessMixdown,
                loudnessPreset: loudnessPreset ? {
                    displayName: loudnessPreset.displayName,
                    target: loudnessPreset.target,
                    range: loudnessPreset.range,
                    peak : loudnessPreset.peak
                } : null,
                keepVoTracks,
                exportMixdown,
                exportSrtFile,
                exportExtendedAD,
                controlTrackLayout,
                controlTrackFadeDb,
                controlTrackFadeInMs,
                controlTrackFadeOutMs,
                maxLines,
                maxChars,
                audioPreset: audioPreset ? {
                    displayName: audioPreset.displayName,
                    format: audioPreset.format,                    
                    samples: audioPreset.samples,
                    resolution: audioPreset.resolution,
                    codec: audioPreset.codec,  
                    bitrate: audioPreset.bitrate,
                } : null,
                autoMixPreset: autoMixPreset ? {
                    displayName: autoMixPreset.displayName ?? false,
                    type: autoMixPreset.type ?? false,
                    threshold: autoMixPreset.threshold ?? false,
                    ratio: autoMixPreset.ratio ?? false,
                    attack: autoMixPreset.attack ?? false,
                    release: autoMixPreset.release ?? false,
                    knee: autoMixPreset.knee ?? false,
                    mix: autoMixPreset.mix ?? false,
                    gainReduction: autoMixPreset.gainReduction ?? false,
                    fadeTime: autoMixPreset.fadeTime ?? false,
                } : null,
                bwfMetadata: {
                    enable: bwfMetadata.enable ?? false,
                    title: bwfMetadata.title ?? false,
                    description: bwfMetadata.description ?? false,
                    referenceTime: bwfMetadata.referenceTime ?? false,
                }
            };

            // Remove undefined values
            Object.keys(config).forEach(key => {
                if (config[key] === undefined) {
                    delete config[key];
                }
            });

            // The preset store resolves the Firebase auth uid itself so that
            // saves land in the same collection loadPresets reads from.
            const preset = await createPreset({
                name: presetName,
                type: presetType,
                config
            });

            toast.push(m.audio_description_export_preset_saved({ name: presetName }), { classes: ["toast-success"] });
            selectedPresetId = preset.id;
        } catch (error) {
            console.error("Error saving preset:", error);
            toast.push(m.audio_description_export_preset_save_failed({ error: error.message }), { classes: ["toast-danger"] });
        }
    }
}

// Function to show load preset dialog
async function showLoadPresetDialog() {
    if (availablePresets.length === 0) {
        await Swal.fire({
            ...SWAL_BASE,
            titleText: m.audio_description_export_preset_none_title(),
            text: m.audio_description_export_preset_none_text(),
            icon: "info",
            confirmButtonText: m.audio_description_export_ok_button(),
            customClass: SWAL_CLASSES.ok,
        });
        return;
    }

    // Create options for the select dropdown
    const presetOptions = {};
    availablePresets.forEach(preset => {
        presetOptions[preset.id] = preset.name;
    });

    const { value: selectedId } = await Swal.fire({
        ...SWAL_BASE,
        titleText: m.audio_description_export_preset_load_title(),
        input: "select",
        inputLabel: m.audio_description_export_preset_load_label(),
        inputOptions: presetOptions,
        inputPlaceholder: m.audio_description_export_preset_load_placeholder(),
        showCancelButton: true,
        confirmButtonText: m.audio_description_export_load_button(),
        cancelButtonText: m.audio_description_export_cancel_button(),
        customClass: SWAL_CLASSES.confirmCancel,
        inputValidator: (value) => {
            if (!value) {
                return m.audio_description_export_preset_select_required();
            }
        }
    });

    if (selectedId) {
        loadPreset(selectedId);
    }
}

// Function to load a preset
function loadPreset(presetId) {
    if (!presetId) return;

    const preset = getPresetById(presetId);
    if (!preset || !preset.config) {
        toast.push(m.audio_description_export_preset_not_found(), { classes: ["toast-warning"] });
        return;
    }

    const config = preset.config;
    
    // Apply config values to state
    if (config.loudnessVo !== undefined) loudnessVo = config.loudnessVo;
    if (config.loudnessProgram !== undefined) loudnessProgram = config.loudnessProgram;
    if (config.loudnessMixdown !== undefined) loudnessMixdown = config.loudnessMixdown;
    if (config.keepVoTracks !== undefined) keepVoTracks = config.keepVoTracks;
    if (config.exportMixdown !== undefined) exportMixdown = config.exportMixdown;
    if (config.exportSrtFile !== undefined) exportSrtFile = config.exportSrtFile;
    if (config.exportExtendedAD !== undefined && !isDubbing) exportExtendedAD = config.exportExtendedAD;
    if (!isDubbing && controlTrackLayouts.some((layout) => layout.id === config.controlTrackLayout)) {
        controlTrackLayout = config.controlTrackLayout;
    }
    if (config.controlTrackFadeDb !== undefined) controlTrackFadeDb = config.controlTrackFadeDb;
    if (config.controlTrackFadeInMs !== undefined) controlTrackFadeInMs = config.controlTrackFadeInMs;
    if (config.controlTrackFadeOutMs !== undefined) controlTrackFadeOutMs = config.controlTrackFadeOutMs;
    if (config.maxLines !== undefined) maxLines = config.maxLines;
    if (config.maxChars !== undefined) maxChars = config.maxChars;
    
    // Restore loudness preset
    if (config.loudnessPreset) {
        const foundPreset = loudnessPresets.find(p => 
            p.displayName === config.loudnessPreset.displayName
        );
        if (foundPreset) {
            loudnessPreset = foundPreset;
        }
    }
    
    // Restore audio preset
    if (config.audioPreset) {
        const foundPreset = audioExportProfiles.find(p => 
            p.displayName === config.audioPreset.displayName
        );
        if (foundPreset) {
            audioPreset = foundPreset;
        }
    }
    
    // Restore auto mix preset
    if (config.autoMixPreset) {
        const foundPreset = autoMixPresets.find(p => 
            p.displayName === config.autoMixPreset.displayName
        );
        if (foundPreset) {
            autoMixPreset = foundPreset;
            // If it's a custom preset, restore the custom values
            if (autoMixPreset.displayName === "Custom") {
                autoMixPreset.type = config.autoMixPreset.type;
                if (config.autoMixPreset.type === "compression") {
                    autoMixPreset.threshold = config.autoMixPreset.threshold;
                    autoMixPreset.ratio = config.autoMixPreset.ratio;
                    autoMixPreset.attack = config.autoMixPreset.attack;
                    autoMixPreset.release = config.autoMixPreset.release;
                    autoMixPreset.makeup = config.autoMixPreset.makeup;
                } else if (config.autoMixPreset.type === "fixedGain") {
                    autoMixPreset.programGainDb = config.autoMixPreset.programGainDb;
                    autoMixPreset.voGainDb = config.autoMixPreset.voGainDb;
                }
            }
        }
    }
    
    // Restore BWF metadata
    if (config.bwfMetadata) {
        bwfMetadata.enable = config.bwfMetadata.enable ?? false;
        if (config.bwfMetadata.title) bwfMetadata.title = config.bwfMetadata.title;
        if (config.bwfMetadata.description) bwfMetadata.description = config.bwfMetadata.description;
    }
    
    selectedPresetId = presetId;
    toast.push(m.audio_description_export_preset_loaded({ name: preset.name }), { classes: ["toast-success"] });
}

/**
 * Updates the progress indicator and status message
 * @param {number} percent - Progress percentage (0-100)
 * @param {string} msg - Status message to display
 */
async function updateProgress(percent, msg) {
    console.log("Updating Job:", percent, msg);
    progress = parseFloat(percent).toFixed(2);
    statusMsg = msg;
}

/**
 * Toggles all accordion sections open or closed
 * @param {boolean} state - True to expand all, false to collapse all
 */
function toggleAllSections(state) {
    showOutputOptions = state;
    showAudioProcessing = state;
    showAdvancedOptions = state;
    showMetadata = state;
}

function calculateExtendedADTimings(events) {
    let cumulativeOffset = 0;
    let adjustedEvents = [];

    events.forEach((event) => {
        let adjustedEvent = JSON.parse(JSON.stringify(event));

        // Apply cumulative offset to start and end times
        adjustedEvent.originalStart = event.start; // Store original for reference
        adjustedEvent.originalEnd = event.end;
        adjustedEvent.start = event.start + cumulativeOffset;
        adjustedEvent.end = event.end + cumulativeOffset;

        // Add this event's duration to the cumulative offset for next events
        let eventDuration = event.end - event.start;
        cumulativeOffset += eventDuration;

        adjustedEvents.push(adjustedEvent);
    });

    totalADDuration = cumulativeOffset;
    return adjustedEvents;
}

/**
 * Opens a folder selector dialog for the export target
 */
function selectTargetFolder() {
    ipcRenderer
        .invoke("selectFolder", {
            title: m.audio_description_export_select_folder_title(),
            properties: ["openDirectory", "createDirectory"],
        })
        .then((path) => {
            console.log(path);
            if (!path.canceled) {
                console.log(path.filePaths[0]);
                targetFolderPath = path.filePaths[0];
                sessionStorage.setItem("audioExportPath", targetFolderPath);
            }
        });
}

/**
 * Handles file selection for custom program audio
 * @param {Event} event - Input change event
 */
async function handleFileSelection(event) {
    files = Array.from(event.target.files || []);
    // Reset customAudioPath if no files selected
    if (!files.length) {
        customAudioPath = null;
        return;
    }

    try {
        // Get the actual file path using Electron's webUtils
        customAudioPath = await webUtils.getPathForFile(files[0]);
        // console.log("Selected program audio file path:", customAudioPath);
    } catch (err) {
        console.error("Error getting file path:", err);
        toast.push(m.audio_description_export_select_audio_failed(), { classes: ["toast-danger"] });
        customAudioPath = null;
        files = [];
    }
}

/**
 * Handles file selection for custom video source
 * @param {Event} event - Input change event
 */
async function handleVideoFileSelection(event) {
    videoFiles = Array.from(event.target.files || []);
    // Reset customVideoPath if no files selected
    if (!videoFiles.length) {
        customVideoPath = null;
        return;
    }

    try {
        // Get the actual file path using Electron's webUtils
        customVideoPath = await webUtils.getPathForFile(videoFiles[0]);
        console.log("Selected video file path:", customVideoPath);
    } catch (err) {
        console.error("Error getting video file path:", err);
        toast.push(m.audio_description_export_select_video_failed(), { classes: ["toast-danger"] });
        customVideoPath = null;
        videoFiles = [];
    }
}

/**
 * Cancels the export job with an error message
 * @param {string} errMessage - Error message to display
 */
function cancelJobWithError(errMessage) {
    toast.push(m.audio_description_export_failed_toast({ error: errMessage }), {
        classes: ["toast-danger"],
    });

    hideModal();
}

/**
 * Main function to begin the export process
 */
async function beginExport() {
    exporting = true;
    updateProgress(0, m.audio_description_export_status_starting());
    toast.push(m.audio_description_export_starting_toast(), { classes: ["toast-info"] });

    /* Update Job Config */
    jobInfo.createdOn = Timestamp.fromDate(new Date());
    jobInfo.config = {
        exportMixdown: exportMixdown,
        exportSrtFile: exportSrtFile,
        keepVoTracks: keepVoTracks,
        exportExtendedAD: exportExtendedAD,
        exportVideo: exportVideo,
        controlTrackLayout: controlTrackLayout,
        autoMix: autoMixPreset.displayName,
        loudness: loudnessPreset.displayName,
        format: audioPreset.displayName,
    };
    /* Done updating job config */

    // Set up file paths for export
    extension = audioPreset.format;
    voAudioPath =
        (process.platform !== "win32" ? path.sep : "") +
        path.join(
            ...targetFolderPath.split(path.sep),
            projectState.name + "_VO_A." + extension,
        );
    mixdownAudioPath =
        (process.platform !== "win32" ? path.sep : "") +
        path.join(
            ...targetFolderPath.split(path.sep),
            projectState.name + "_MIXDOWN." + extension,
        );
    voAudioPathB =
        (process.platform !== "win32" ? path.sep : "") +
        path.join(
            ...targetFolderPath.split(path.sep),
            projectState.name + "_VO_B." + extension,
        );
    voAudioPathFinal =
        (process.platform !== "win32" ? path.sep : "") +
        path.join(
            ...targetFolderPath.split(path.sep),
            projectState.name + "_VO." + extension,
        );
    srtFileBasePath =
        (process.platform !== "win32" ? path.sep : "") +
        path.join(...targetFolderPath.split(path.sep), projectState.name);

    // Control track output is always a WAV, whatever the audio format
    const controlTrackLayoutConfig = controlTrackLayouts.find((layout) => layout.id === controlTrackLayout);
    controlTrackPath = controlTrackLayoutConfig?.fileSuffix
        ? (process.platform !== "win32" ? path.sep : "") +
          path.join(
              ...targetFolderPath.split(path.sep),
              projectState.name + controlTrackLayoutConfig.fileSuffix + ".wav",
          )
        : null;

    // Set up video output path if video export is enabled
    if (exportVideo) {
        videoOutputPath =
            (process.platform !== "win32" ? path.sep : "") +
            path.join(
                ...targetFolderPath.split(path.sep),
                projectState.name + "_VIDEO_AD.mp4",
            );
    }

    /* Get index of the event group to export */
    eventGroupIndex = eventGroupState.findIndex((evGroup) => {
        return evGroup.id === eventGroupId;
    });

    // Sort events by time and create a deep copy
    eventGroupState[eventGroupIndex] = orderByTime(
        eventGroupState[eventGroupIndex],
    );
    copyOfEventGroup = JSON.parse(
        JSON.stringify(eventGroupState[eventGroupIndex]),
    );

    if (exportExtendedAD) {
        // Calculate extended AD timings
        extendedADEvents = calculateExtendedADTimings(copyOfEventGroup.events);

        // Update the copyOfEventGroup with adjusted timings for export
        copyOfEventGroup.events = extendedADEvents;
    }

    // Remove HTML entities from text
    copyOfEventGroup = removeHtmlEntities(copyOfEventGroup);

    // For trial mode, limit to first 10 events
    if (authState.status === "in_trial") {
        // Only include the first 10 events in the export
        copyOfEventGroup.events = copyOfEventGroup.events.slice(0, 10);
    }

    // Validate selection bounds
    if (selection && selectionStart > copyOfEventGroup.events.length) {
        cancelJobWithError(
            m.audio_description_export_selection_start_invalid(),
        );
    }

    if (selection && selectionEnd > copyOfEventGroup.events.length) {
        cancelJobWithError(
            m.audio_description_export_selection_end_invalid(),
        );
    }

    // Run pre-export validation checks
    let preCheckResult = await preExportCheck(copyOfEventGroup.events);

    if (preCheckResult.err) {
        cancelExport(eventGroupIndex, preCheckResult.eventId);
    } else {
        getDuration();
    }
}

/**
 * Get the duration of the media file
 * Used to determine the length of the output audio files
 */
async function getDuration() {
    try {
        // Use player duration if available
        if (playerState.duration) {
            duration = playerState.duration;
        } else {
            // Otherwise, interrogate media file for duration using ffmpeg
            console.info(
                "Media Duration not set. Interrogating media file for duration...",
            );
            var matches;
            try {
                let ffmpegRes = await exec(
                    `${window.ffmpegPath.replace("app.asar", "app.asar.unpacked")} -y -i "${projectState.media.localPath}`,
                );
                matches = ffmpegRes.match(
                    /(?<=duration\:\s)\d\d:\d\d:\d\d\.\d\d/im,
                );
            } catch (err) {
                // ffmpeg typically returns info in the error stream
                matches = err.message.match(
                    /(?<=duration\:\s)\d\d:\d\d:\d\d\.\d\d/im,
                );
            }

            if (matches) {
                duration = tcLib.tcMsToSec(matches[0]);
                console.log("Duration detected from file:", duration);
            }
        }

        if (duration) {
            if (exportExtendedAD) {
                duration += totalADDuration;
                console.log(
                    `Extended AD enabled. Original duration: ${duration - totalADDuration}s, Extended duration: ${duration}s`,
                );
            }

            await updateProgress(5, m.audio_description_export_status_rendering_event_audio());

            // Check if any events extend beyond media duration
            let eventLongerThanDuration = eventGroupState[
                projectState.selected
            ].events.findIndex((event, index) => {
                if (selection) {
                    return (
                        (event.start > duration || event.end > duration) &&
                        index + 1 >= selectionStart &&
                        index + 1 <= selectionEnd
                    );
                } else {
                    return event.start > duration || event.end > duration;
                }
            });

            if (eventLongerThanDuration !== -1) {
                const durationError = m.audio_description_export_event_time_exceeds_duration({
                    index: eventLongerThanDuration + 1,
                    duration: duration,
                });
                console.error(durationError);
                cancelJobWithError(durationError);
            } else if (exportMixdown || keepVoTracks || exportSrtFile) {
                // Process according to selected export options
                if (exportSrtFile) {
                    exportSrt(copyOfEventGroup, keepVoTracks, srtFileBasePath);
                }

                if (exportMixdown || keepVoTracks) {
                    startRenderingEvents();
                } else {
                    endExportProcess();
                }
            } else {
                cancelJobWithError(m.audio_description_export_no_options_error());
            }
        } else {
            cancelJobWithError(
                m.audio_description_export_duration_unknown_error(),
            );
        }
    } catch (err) {
        console.log(err, err.message);
        console.log("---USING THE LAST EVENT END TIME---");
        // Fallback: Use the end time of the last event + 10 seconds
        duration =
            copyOfEventGroup.events[copyOfEventGroup.events.length - 1].end +
            10;

        if (exportExtendedAD) {
            duration += totalADDuration;
        }

        if (exportMixdown || keepVoTracks || exportSrtFile) {
            if (exportSrtFile) {
                exportSrt(copyOfEventGroup, keepVoTracks, srtFileBasePath);
            }

            if (exportMixdown || keepVoTracks) {
                startRenderingEvents();
            } else {
                endExportProcess();
            }
        } else {
            cancelJobWithError(m.audio_description_export_no_options_error());
        }
    }
}

/**
 * Start the process of rendering audio for each event
 * This sets up the event queue and starts the initial batch of renders
 */
function startRenderingEvents() {
    try {
        // Filter events based on selection range if needed
        if (selection) {
            eventQueue = copyOfEventGroup.events.filter((event, index) => {
                return index + 1 >= selectionStart && index + 1 <= selectionEnd;
            });
        } else {
            eventQueue = JSON.parse(JSON.stringify(copyOfEventGroup.events));
        }

        totalEvents = eventQueue.length;

        // Check if any events use ElevenLabs (which requires different batch size)
        let elevenLabsEventIndex = eventQueue.findIndex((evt) => {
            return evt.voice.provider === "ElevenLabs";
        });

        // Start rendering with appropriate batch size
        if (elevenLabsEventIndex > -1) {
            // ElevenLabs has lower rate limits, so use smaller batches
            renderAudio(
                eventQueue.splice(
                    0,
                    eventQueue.length > 2 ? 2 : eventQueue.length,
                ),
            );
        } else {
            renderAudio(
                eventQueue.splice(
                    0,
                    eventQueue.length > renderQueueSize
                        ? renderQueueSize
                        : eventQueue.length,
                ),
            );
        }
    } catch (err) {
        cancelJobWithError(err.message);
    }
}

/**
 * Renders audio for a batch of events
 * @param {Array} queue - Array of events to process in this batch
 */
function renderAudio(queue) {
    // console.log("Rendering audio for event batch:", queue);
    try {
        updateProgress(
            (((totalEvents - eventQueue.length) / totalEvents) * 50).toFixed(2),
            m.audio_description_export_status_rendering_batch({
                start: totalEvents - eventQueue.length,
                end: Math.min(totalEvents - eventQueue.length + renderQueueSize - 1, totalEvents),
                total: totalEvents
            }),
        );

        let renderTasks = [];
        queue.forEach(async (event) => {
            let plainText = voiceUtils.prepareTtsText(convertToPlainText(event.text, " "), event.voice, event.speakingStyle);
            /* update the job config */
            jobInfo.chars += plainText.length ?? 5;

            try {
                    // Check if event already has an audio file that can be reused.
                    // An empty audioFile means there is nothing to reuse and the
                    // event must be rendered fresh via TTS.
                    let audioFile =
                        typeof event.audioFile === "string"
                            ? event.audioFile.trim()
                            : "";
                    let isRemoteAudio = /^https?:\/\//i.test(audioFile);
                    let localFileExists = false;
                    if (audioFile && !isRemoteAudio) {
                        try {
                            fsSync.accessSync(audioFile);
                            localFileExists = true;
                            console.log("FILE EXISTS ALREADY");
                        } catch (err) {
                            console.log("FILE DOES NOT EXISTS");
                            console.log(err, err.message);
                        }
                    }

                    if (event.voice && isRemoteAudio) {
                        // Reuse existing audio: refresh the signed URL first so
                        // expiry does not fall through to billed TTS.
                        renderTasks.push(
                            (async () => {
                                const live = eventGroupState[eventGroupIndex].events.find(
                                    (liveEvent) => liveEvent.id === event.id,
                                );
                                if (live && getEventAudioStoragePath(live)) {
                                    const ok = await refreshEventAudioUrl(live);
                                    if (ok) event.audioFile = live.audioFile;
                                }
                                return downloadPromiseInsteadOfRender({
                                    event: event,
                                    eventGroupId:
                                        eventGroupState[eventGroupIndex].id,
                                    eventId: event.id,
                                });
                            })(),
                        );
                    } else if (event.voice && localFileExists) {
                        // Reuse existing audio file (copy local file)
                        renderTasks.push(
                            copyPromiseInsteadOfRender({
                                event: event,
                                eventGroupId:
                                    eventGroupState[eventGroupIndex].id,
                                eventId: event.id,
                            }),
                        );
                    } else {
                        // Generate new audio using cloud TTS service
                        renderTasks.push(
                            httpsCallable(functions, "v8TextToSpeechProviderV5")({
                                provider: event.voice.provider,
                                language: event.voice.language,
                                voice: event.voice.name,
                                voice_id: event.voice.id,
                                engine: event.voice.engine,
                                model: voiceUtils.renderModel(event.voice, event.speakingStyle),
                                stability: event.voiceStability / 100,
                                similarity: event.voiceSimilarity / 100,
                                speed: 1,
                                style: voiceUtils.renderStyle(event.voice, event.speakingStyle),
                                audioEncoding:
                                    extension === "mp3" ? "mp3" : "wav",
                                text: plainText,
                                eventGroupId:
                                    eventGroupState[eventGroupIndex].id,
                                eventId: event.id,
                            }),
                        );
                    }
                } catch (err) {
                    console.log("ERROR - FAILED CALL TO RENDER CLOUD");
                    console.log(err, err.message);
                    if (!retryFlag) {
                        console.log("FAILED... Adding event to retry queue!");
                        retryQueue.push(event);
                    } else {
                        console.log(err, err.message);
                        cancelJobWithError(err.message);
                    }
                }
        });

        // Process all rendering tasks in the current batch
        Promise.allSettled(renderTasks)
            .then(async (results) => {
                let eventIndex, audioFilePath;
                // console.log(results);
                // Process the results for each event
                for (let i = 0; i < results.length; i++) {
                    if (results[i].status === "rejected") {
                        console.log(
                            "Text to speech process failed:",
                            results[i],
                        );
                        throw new Error(
                            "Rendering Event audio failed. " +
                                results[i].reason.code,
                        );
                    } else if (results[i].value.data) {
                        // Find the event in the event group
                        eventIndex = eventGroupState[
                            eventGroupIndex
                        ].events.findIndex((event) => {
                            return event.id === results[i].value.data.eventId;
                        });

                        // Create path for the audio file in target folder
                        audioFilePath =
                            (process.platform !== "win32" ? path.sep : "") +
                            path.join(
                                ...targetFolderPath.split(path.sep),
                                eventIndex + "." + extension,
                            );

                        if (results[i].value.data.copy) {
                            // Copy existing audio file to target folder
                            if (!results[i].value.data.audio) {
                                throw new Error(
                                    `Audio file is missing for Event ${eventIndex + 1}. Please re-render the Event audio and try again.`,
                                );
                            }
                            console.log(
                                "Copying file from",
                                results[i].value.data.audio,
                                "to",
                                audioFilePath,
                            );
                            if (results[i].value.data.audio !== audioFilePath) {
                                await fs.copyFile(
                                    results[i].value.data.audio,
                                    audioFilePath,
                                );
                            }
                        } else {
                            // Download audio file from URL to target folder
                            try {
                                await writeFileStream(
                                    results[i].value.data.audio,
                                    audioFilePath,
                                );
                            } catch (downloadErr) {
                                const liveEvent =
                                    eventGroupState[eventGroupIndex].events[eventIndex];
                                if (!liveEvent?.voice) throw downloadErr;
                                const plainText = voiceUtils.prepareTtsText(
                                    convertToPlainText(liveEvent.text, " "),
                                    liveEvent.voice,
                                    liveEvent.speakingStyle,
                                );
                                const ttsRes = await httpsCallable(
                                    functions,
                                    "v8TextToSpeechProviderV5",
                                )({
                                    provider: liveEvent.voice.provider,
                                    language: liveEvent.voice.language,
                                    voice: liveEvent.voice.name,
                                    voice_id: liveEvent.voice.id,
                                    engine: liveEvent.voice.engine,
                                    model: voiceUtils.renderModel(
                                        liveEvent.voice,
                                        liveEvent.speakingStyle,
                                    ),
                                    stability: liveEvent.voiceStability / 100,
                                    similarity: liveEvent.voiceSimilarity / 100,
                                    speed: 1,
                                    style: voiceUtils.renderStyle(
                                        liveEvent.voice,
                                        liveEvent.speakingStyle,
                                    ),
                                    audioEncoding:
                                        extension === "mp3" ? "mp3" : "wav",
                                    text: plainText,
                                    eventGroupId:
                                        eventGroupState[eventGroupIndex].id,
                                    eventId: liveEvent.id,
                                });
                                if (!ttsRes?.data?.audio) throw downloadErr;
                                await writeFileStream(
                                    ttsRes.data.audio,
                                    audioFilePath,
                                );
                                const duration = ttsRes.data.duration;
                                await assignEventAudio(liveEvent, {
                                    audioFile: ttsRes.data.audio,
                                    audioFileDuration:
                                        typeof duration === "number"
                                            ? duration / 1000
                                            : tcLib.tcMsToSec(duration),
                                    audioFileRemote: ttsRes.data.storagePath,
                                });
                            }
                        }

                        // console.log("Tracking generated audio file:", audioFilePath);
                        // Track the generated audio file
                        voAudioFiles.push({
                            eventIndex: eventIndex,
                            audioFilePath: audioFilePath,
                        });
                    }
                }

                // Process next batch or move to stitching stage
                if (eventQueue.length > 0) {
                    // Continue with next batch
                    let elevenLabsEventIndex = eventQueue.findIndex((evt) => {
                        return evt.voice.provider === "ElevenLabs";
                    });

                    if (elevenLabsEventIndex > -1) {
                        renderAudio(
                            eventQueue.splice(
                                0,
                                eventQueue.length > 2 ? 2 : eventQueue.length,
                            ),
                        );
                    } else {
                        renderAudio(
                            eventQueue.splice(
                                0,
                                eventQueue.length > renderQueueSize
                                    ? renderQueueSize
                                    : eventQueue.length,
                            ),
                        );
                    }
                } else if (retryQueue.length > 0) {
                    // Process any events that need to be retried
                    console.log("RUNNING THE RETRY QUEUE!");
                    retryFlag = true;
                    let elevenLabsEventIndex = retryQueue.findIndex((evt) => {
                        return evt.voice.provider === "ElevenLabs";
                    });

                    if (elevenLabsEventIndex > -1) {
                        renderAudio(
                            retryQueue.splice(
                                0,
                                retryQueue.length > 2 ? 2 : retryQueue.length,
                            ),
                        );
                    } else {
                        renderAudio(
                            retryQueue.splice(
                                0,
                                retryQueue.length > renderQueueSize
                                    ? renderQueueSize
                                    : retryQueue.length,
                            ),
                        );
                    }
                } else {
                    // All events rendered, move to stitching phase
                    console.log("Starting audio stitch process");
                    stitchAudioFiles();
                }
            })
            .catch((err) => {
                console.log(err, err.message);
                cancelJobWithError(err.message);
            });
    } catch (err) {
        console.log(err, err.message);
        cancelJobWithError(err.message);
    }
}

/**
 * Helper function to create a promise for downloading audio files
 * Used as an alternative to rendering new audio
 */
function downloadPromiseInsteadOfRender(data) {
    return new Promise((resolve, reject) => {
        resolve({
            data: {
                eventGroupId: data.eventGroupId,
                eventId: data.eventId,
                audio: data.event.audioFile,
                duration: data.event.audioFileDuration,
                copy: false,
            },
        });
    });
}

/**
 * Helper function to create a promise for copying local audio files
 * Used as an alternative to rendering new audio
 */
function copyPromiseInsteadOfRender(data) {
    return new Promise((resolve, reject) => {
        resolve({
            data: {
                eventGroupId: data.eventGroupId,
                eventId: data.eventId,
                audio: data.event.audioFile,
                duration: data.event.audioFileDuration,
                copy: true,
            },
        });
    });
}

/**
 * Stitches together individual audio files into a continuous VO track
 * Uses FFmpeg complex filters to position each audio clip at its proper timestamp
 */
function stitchAudioFiles() {
    let ffmpegCmd = ffmpeg(),
        filterString = "",
        audioTracks = [],
        i = 0;

    loops++;

    // If this is not the first pass, include the previously generated audio
    if (loops > 1) {
        ffmpegCmd.addInput(loops % 2 === 0 ? voAudioPath : voAudioPathB);
        filterString = `[0]adelay=0|0[a0];`;
        audioTracks.push(`[a0]`);
        i++;
    }

    // Add each audio file with its correct timing
    for (i; i < stitchQueueSize; i++) {
        if (count < totalEvents) {
            console.log(voAudioFiles);
            console.log(`Stitching Event ${count} of ${totalEvents}`);
            console.log(
                voAudioFiles[count],
                voAudioFiles[count].audioFilePath,
                voAudioFiles[count].eventIndex,
            );
            console.log(
                copyOfEventGroup.events[voAudioFiles[count].eventIndex].start,
            );
            ffmpegCmd.addInput(voAudioFiles[count].audioFilePath);
            const stitchEvent = copyOfEventGroup.events[voAudioFiles[count].eventIndex];
            const delayMs = Math.max(0, parseInt(stitchEvent.start * 1000));
            // Apply the event's playback speed, then position the clip at its event start time (in milliseconds)
            const tempo = voiceUtils.atempoChain(stitchEvent.speed);
            filterString += `[${i}]${tempo ? tempo + "," : ""}adelay=${delayMs}|${delayMs}[a${i}];`;
            audioTracks.push(`[a${i}]`);
            count++;
        }
    }

    // Mix all audio tracks together
    filterString += `${audioTracks.join("")}amix=inputs=${audioTracks.length}:normalize=disabled[master]`;

    // Configure audio output format
    ffmpegCmd.audioCodec(audioPreset.codec);
    ffmpegCmd.audioChannels(1);

    if (audioPreset.format === "mp3") {
        ffmpegCmd.audioBitrate(audioPreset.bitrate);
    } else {
        ffmpegCmd.audioFrequency(audioPreset.samples);
    }

    // Execute FFmpeg command to stitch audio
    ffmpegCmd
        .complexFilter(`${filterString}`)
        .outputOptions(`-map [master]`)
        .output(loops % 2 === 0 ? voAudioPathB : voAudioPath)
        .on("start", function (commandLine) {
            console.log("Spawned Ffmpeg with command: " + commandLine);
            updateProgress(
                60,
                m.audio_description_export_status_stitching_vo({
                    current: count,
                    total: totalEvents,
                    timecode: "00:00:00.00"
                }),
            );
        })
        .on("progress", function (progress) {
            /* progress = {percent, timemark, currentFps, frames, targetSize, currentKbps} */
            console.log("Exporting", progress.timemark);
            updateProgress(
                65,
                m.audio_description_export_status_stitching_vo({
                    current: count,
                    total: totalEvents,
                    timecode: progress.timemark
                }),
            );
        })
        .on("end", function () {
            console.log("Finished Exporting");
            // Continue to next batch or finalize the VO track
            if (count < voAudioFiles.length) {
                stitchAudioFiles();
            } else {
                exportVoTrack();
            }
        })
        .on("error", function (err, stdout, stderr) {
            console.error(
                `Error exporting VO Track ${count}. ` + err.message,
                err,
            );
            updateProgress(
                100,
                m.audio_description_export_status_vo_failed_at({ index: count }),
                true,
            );
            console.error(stderr);
            console.error(stdout);
            toast.push(m.audio_description_export_vo_track_failed(), {
                classes: ["toast-danger"],
            });

            cancelJobWithError(err.message);
        })
        .run();
}

/**
 * Apply loudness normalization to an audio file using a two-pass process
 * @param {string} sourceFilePath - Path to source audio file
 * @param {string} targetFilePath - Path to save normalized audio
 * @param {Object} audioCodecOptions - Audio codec options for output (codec, channels, etc)
 * @param {number} integratedLoudness - Target integrated loudness in LUFS
 * @param {number} loudnessRange - Target loudness range
 * @param {number} maxTruePeak - Maximum true peak in dB
 * @param {function} progressCallback - Function to call with progress updates (percent, message)
 * @returns {Promise<void>}
 */
async function applyLoudnessNormalization(
    sourceFilePath,
    targetFilePath,
    audioCodecOptions,
    integratedLoudness,
    loudnessRange,
    maxTruePeak,
    progressCallback = () => {},
) {
    try {
        // First pass - analyze loudness
        progressCallback(0, m.audio_description_export_loudness_starting());

        // Run the first pass using exec to capture stderr
        const firstPassCmd = `"${window.ffmpegPath.replace("app.asar", "app.asar.unpacked")}" -y -i "${sourceFilePath}" -af loudnorm=I=${integratedLoudness}:LRA=${loudnessRange}:TP=${maxTruePeak}:print_format=json -f null -`;

        // Execute first pass and capture stderr for analysis
        let analysisStderr = "";
        try {
            progressCallback(10, m.audio_description_export_loudness_analyzing());

            await new Promise((resolve, reject) => {
                const process = exec(
                    firstPassCmd,
                    { maxBuffer: 10 * 1024 * 1024 },
                    (error, stdout, stderr) => {
                        // ffmpeg writes to stderr even on success
                        analysisStderr = stderr;

                        // For null output, ffmpeg often exits with code 1, so we check if we got usable data instead
                        if (
                            stderr.includes("loudnorm") &&
                            stderr.includes("input_")
                        ) {
                            resolve();
                        } else if (error && error.code !== 1) {
                            // ffmpeg null output typically returns code 1
                            reject(error);
                        } else if (!stderr.includes("loudnorm")) {
                            reject(
                                new Error(
                                    "Loudness analysis did not produce expected output",
                                ),
                            );
                        } else {
                            resolve();
                        }
                    },
                );
            });

            progressCallback(40, m.audio_description_export_loudness_processing());
        } catch (err) {
            console.error("Error running first pass:", err);
            throw new Error(`Failed to analyze audio loudness: ${err.message}`);
        }

        // Extract the JSON data from the stderr output
        let analysisData;
        try {
            // Find the JSON block in stderr
            const jsonStart = analysisStderr.lastIndexOf("{");
            const jsonEnd = analysisStderr.lastIndexOf("}");

            if (jsonStart === -1 || jsonEnd === -1 || jsonStart > jsonEnd) {
                throw new Error(
                    "Could not find valid JSON in loudness analysis output",
                );
            }

            const jsonStr = analysisStderr.substring(jsonStart, jsonEnd + 1);
            analysisData = JSON.parse(jsonStr);
            console.log("Loudness analysis data:", analysisData);

            // Validate the data
            if (
                !analysisData.input_i ||
                !analysisData.input_lra ||
                !analysisData.input_tp ||
                !analysisData.input_thresh ||
                !analysisData.target_offset
            ) {
                throw new Error("Incomplete loudness analysis data");
            }
        } catch (err) {
            console.error("Error parsing loudness stats:", err);
            throw new Error(
                `Failed to parse loudness analysis data: ${err.message}`,
            );
        }

        progressCallback(
            50,
            "Loudness analysis complete, applying normalization...",
        );

        // Second pass - apply loudness normalization
        return new Promise((resolve, reject) => {
            const ffmpegCmd = ffmpeg();

            // Set up input
            ffmpegCmd.input(sourceFilePath);

            // Configure audio parameters from the passed options
            if (audioCodecOptions) {
                if (audioCodecOptions.codec) {
                    ffmpegCmd.audioCodec(audioCodecOptions.codec);
                }

                if (audioCodecOptions.channels) {
                    ffmpegCmd.audioChannels(audioCodecOptions.channels);
                }

                if (
                    audioCodecOptions.format === "mp3" &&
                    audioCodecOptions.bitrate
                ) {
                    ffmpegCmd.audioBitrate(audioCodecOptions.bitrate);
                } else if (audioCodecOptions.samples) {
                    ffmpegCmd.audioFrequency(audioCodecOptions.samples);
                }
            }

            // Apply the loudnorm filter with measured values
            ffmpegCmd.audioFilters(
                `loudnorm=I=${integratedLoudness}:LRA=${loudnessRange}:TP=${maxTruePeak}:` +
                    `measured_I=${analysisData.input_i}:` +
                    `measured_LRA=${analysisData.input_lra}:` +
                    `measured_TP=${analysisData.input_tp}:` +
                    `measured_thresh=${analysisData.input_thresh}:` +
                    `offset=${analysisData.target_offset}`,
            );

            // Set output and event handlers
            ffmpegCmd
                .output(targetFilePath)
                .on("start", function (commandLine) {
                    console.log("Second pass FFmpeg command:", commandLine);
                })
                .on("progress", function (progress) {
                    // Map progress from 50-100%
                    const adjustedProgress = 50 + (progress.percent || 0) / 2;
                    progressCallback(
                        adjustedProgress,
                        `Applying loudness normalization... ${progress.timemark || "00:00:00"}`,
                    );
                })
                .on("error", function (err) {
                    console.error("Error in second pass:", err.message);
                    reject(err);
                })
                .on("end", function () {
                    progressCallback(100, m.audio_description_export_loudness_complete());
                    resolve();
                })
                .run();
        });
    } catch (err) {
        console.error("Loudness normalization failed:", err);
        throw err;
    }
}

/**
 * Exports the final voice over track by padding to full duration
 * and applying optional loudness normalization
 */
async function exportVoTrack() {
    /* ffmpeg -i C:\Users\natha\Videos\DVTEST\0.mp3 -i C:\Users\natha\Videos\DVTEST\1.mp3 -i C:\Users\natha\Videos\DVTEST\2.mp3 -y -filter_complex [0]adelay=1515|1515[a0];[1]adelay=6734|6734[a1];[2]adelay=16140|16140[a2];[a0][a1][a2]amix=inputs=3:normalize=disabled[dv];[dv]apad=whole_dur=52209ms[master] -acodec pcm_s24le -ac 2 -ar 48000 -map [master] C:\Users\natha\Videos\DVTEST\Audio Description Project_VO.wav */

    try {
        console.log("Exporting VO Track");
        const sourceFile = loops % 2 === 0 ? voAudioPathB : voAudioPath;
        const tempOutputFile = sourceFile + ".temp." + extension;
        const finalOutputFile = loops % 2 === 0 ? voAudioPath : voAudioPathB;

        console.log("Using VO Track:", sourceFile);

        // Step 1: Pad audio to full duration without loudness normalization
        await new Promise((resolve, reject) => {
            let ffmpegCmd = ffmpeg();
            let filterString = `apad=whole_dur=${parseInt(duration * 1000)}ms[master]`;

            ffmpegCmd.addInput(sourceFile);

            // Configure audio output format
            ffmpegCmd.audioCodec(audioPreset.codec);
            ffmpegCmd.audioChannels(1);

            if (audioPreset.format === "mp3") {
                ffmpegCmd.audioBitrate(audioPreset.bitrate);
            } else {
                ffmpegCmd.audioFrequency(audioPreset.samples);
            }

            ffmpegCmd
                .complexFilter(filterString)
                .outputOptions(`-map [master]`)
                .output(tempOutputFile)
                .on("start", function (commandLine) {
                    console.log("Spawned Ffmpeg with command: " + commandLine);
                    updateProgress(70, m.audio_description_export_status_preparing_vo());
                })
                .on("progress", function (progress) {
                    console.log("Exporting", progress.timemark);
                    updateProgress(
                        72,
                        `VO Track preparation... ${progress.timemark}`,
                    );
                })
                .on("end", function () {
                    resolve();
                })
                .on("error", function (err, stdout, stderr) {
                    console.error(
                        "Error exporting VO Track. " + err.message,
                        err,
                    );
                    console.error(stderr);
                    console.error(stdout);
                    reject(err);
                })
                .run();
        });

        // Step 2: Apply loudness normalization if enabled
        if (loudnessVo && loudnessEnabled) {
            // Audio codec options for the loudness normalization
            const audioCodecOptions = {
                codec: audioPreset.codec,
                channels: 1,
                format: audioPreset.format,
                bitrate:
                    audioPreset.format === "mp3" ? audioPreset.bitrate : null,
                samples:
                    audioPreset.format !== "mp3" ? audioPreset.samples : null,
            };

            // Apply loudness normalization
            await applyLoudnessNormalization(
                tempOutputFile,
                finalOutputFile,
                audioCodecOptions,
                loudnessPreset.target,
                loudnessPreset.range,
                loudnessPreset.peak,
                (percent, msg) => {
                    // Map progress from 0-100 to 73-85
                    const mappedPercent = 73 + percent * 0.12;
                    updateProgress(mappedPercent, msg);
                },
            );

            // Clean up temp file
            await fs.unlink(tempOutputFile);
        } else {
            // Just rename the temp file to final output
            await fs.rename(tempOutputFile, finalOutputFile);
            updateProgress(85, m.audio_description_export_status_vo_complete());
        }

        console.log("Finished Exporting VO Track");

        if (controlTrackPath) {
            await exportControlTrackFile(finalOutputFile);
        }

        // Move to mixdown phase if enabled, otherwise finish export
        if (exportMixdown) {
            exportMixdownFile();
        } else {
            endExportProcess();
        }
    } catch (err) {
        console.error("Error exporting VO Track. " + err.message, err);
        updateProgress(100, m.audio_description_export_status_vo_failed(), true);
        console.error(err);
        toast.push(m.audio_description_export_vo_track_failed(), {
            classes: ["toast-danger"],
        });

        cancelJobWithError(err.message);
    }
}

/**
 * Exports the AD narration with a WHP 198 control track (the BBC "audio
 * description studio signal"): a 48 kHz, 24-bit WAV with the mono VO on
 * channel 1 and the fade/pan data signal on channel 2. A failure here is
 * reported but does not stop the rest of the export.
 *
 * @param {string} voTrackPath - Padded (and normalised) VO track
 */
async function exportControlTrackFile(voTrackPath) {
    const rawControlPath = controlTrackPath + ".control.temp.pcm";
    try {
        await updateProgress(85, m.audio_description_export_status_control_track());

        const events = (selection
            ? copyOfEventGroup.events.filter((event, index) => index + 1 >= selectionStart && index + 1 <= selectionEnd)
            : copyOfEventGroup.events
        ).map((event) => {
            // Cover the whole narration if the rendered audio runs past the event end
            const speed = event.speed > 0 ? event.speed : 1;
            const audioEnd = event.audioFileDuration > 0 ? event.start + event.audioFileDuration / speed : event.end;
            return { start: event.start, end: Math.max(event.end, audioEnd) };
        });

        const renderer = createControlTrackRenderer({
            events,
            durationSec: duration,
            fadeDb: Number(controlTrackFadeDb),
            fadeInMs: Number(controlTrackFadeInMs),
            fadeOutMs: Number(controlTrackFadeOutMs),
            sampleRate: CONTROL_TRACK_SAMPLE_RATE,
        });

        // Render the data signal to raw 16-bit PCM in chunks, decoding it as
        // we go so a bad signal is caught before it reaches a deliverable.
        const decoder = new Whp198Decoder(CONTROL_TRACK_SAMPLE_RATE);
        let decodedPackets = 0;
        let mismatches = 0;
        const fd = fsSync.openSync(rawControlPath, "w");
        try {
            for (const chunk of renderer.chunks()) {
                fsSync.writeSync(fd, new Uint8Array(chunk.buffer, chunk.byteOffset, chunk.byteLength));
                for (const packet of decoder.push(chunk)) {
                    const index = Math.round(packet.startSample / (CONTROL_TRACK_SAMPLE_RATE / 10));
                    if (!packet.crcOk || packet.fade !== renderer.fadeBytes[index]) mismatches++;
                    decodedPackets++;
                }
                // Let the progress UI update between chunks
                await new Promise((resolve) => setTimeout(resolve, 0));
            }
        } finally {
            fsSync.closeSync(fd);
        }

        const expectedPackets = Math.floor(duration * 10);
        if (mismatches > 0 || decodedPackets < expectedPackets - 2) {
            console.error("Control track self-check failed", { decodedPackets, expectedPackets, mismatches });
            throw new Error(m.audio_description_export_control_track_check_failed());
        }

        // Channel 1: VO resampled to 48 kHz mono. Channel 2: the data signal,
        // merged untouched (no loudness, gain or resampling).
        await new Promise((resolve, reject) => {
            ffmpeg()
                .addInput(voTrackPath)
                .addInput(rawControlPath)
                .inputOptions(["-f s16le", `-ar ${CONTROL_TRACK_SAMPLE_RATE}`, "-ac 1"])
                .complexFilter(
                    `[0:a]aresample=${CONTROL_TRACK_SAMPLE_RATE},aformat=channel_layouts=mono[ad];[ad][1:a]amerge=inputs=2[master]`,
                )
                .outputOptions(["-map [master]"])
                .audioCodec("pcm_s24le")
                .audioFrequency(CONTROL_TRACK_SAMPLE_RATE)
                .output(controlTrackPath)
                .on("start", (commandLine) => console.log("Spawned Ffmpeg with command: " + commandLine))
                .on("end", () => resolve())
                .on("error", (err, stdout, stderr) => {
                    console.error("Error creating control track file. " + err.message, stderr);
                    reject(err);
                })
                .run();
        });

        if (bwfMetadata.enable) {
            embedBwfMetadata(controlTrackPath, CONTROL_TRACK_SAMPLE_RATE);
        }
        console.log("Finished Exporting AD Control Track", { decodedPackets, fadeByte: renderer.fadeByte });
    } catch (err) {
        console.error("Error exporting AD control track. " + err.message, err);
        toast.push(m.audio_description_export_control_track_failed({ error: err.message }), {
            classes: ["toast-danger"],
        });
    } finally {
        await fs.unlink(rawControlPath).catch(() => {});
    }
}

/**
 * Exports the final mixdown combining the VO track with program audio
 * Applies sidechain compression to duck program audio during VO segments
 */
/**
 * Asynchronously exports a final mixdown audio file with audio description.
 * This function handles two modes: standard AD with sidechain compression and
 * extended AD where the program audio is cut and shifted to accommodate the AD.
 *
 * @async
 * @throws {Error} If any part of the ffmpeg process or file system operations fail.
 */
async function exportMixdownFile() {
    try {
        // Configure audio output format

        const audioCodecOptions = {
            codec: audioPreset.codec,
            channels: 2,
            format: audioPreset.format,
            bitrate: audioPreset.format === "mp3" ? audioPreset.bitrate : null,
            samples: audioPreset.format !== "mp3" ? audioPreset.samples : null
        }
        // Define file paths
        const tempMixdownPath = mixdownAudioPath + ".temp." + extension;
        const tempProgramAudioPath = mixdownAudioPath + ".program.temp." + extension;

        // Rename final VO file and clean up temporary files
        if (loops % 2 === 0) {
            await fs.rename(voAudioPath, voAudioPathFinal);
            await fs.unlink(voAudioPathB);
        } else {
            await fs.rename(voAudioPathB, voAudioPathFinal);
            await fs.unlink(voAudioPath);
        }

        await updateProgress(86, m.audio_description_export_status_creating_mixdown());

        // Get the actual file path to use for program audio
        const programAudioPath = customAudioPath || projectState.media.localPath;
        console.log("Using program audio:", programAudioPath);

        // STEP 1: PROCESS THE AUDIO BASED ON PRESET TYPE AND MODE
        if (autoMixPreset.type === "fixedGain" && !exportExtendedAD) {
            // For fixed gain presets (non-extended AD), use a segment-based approach
            
            // First, analyze the events and create timeline segments
            const events = copyOfEventGroup.events.sort((a, b) => a.start - b.start);
            const segments = [];
            let currentTime = 0;
            
            events.forEach((event, idx) => {
                // Add segment before the event (normal volume)
                if (currentTime < event.start) {
                    segments.push({
                        start: currentTime,
                        end: event.start,
                        type: 'normal',
                        index: segments.length
                    });
                }
                
                // Add the event segment (reduced volume)
                segments.push({
                    start: event.start,
                    end: event.end,
                    type: 'reduced',
                    index: segments.length
                });
                
                currentTime = event.end;
            });
            
            // Add final segment after last event (normal volume)
            if (currentTime < duration) {
                segments.push({
                    start: currentTime,
                    end: duration,
                    type: 'normal',
                    index: segments.length
                });
            }
            
            console.log("Timeline segments:", segments);
            
            // Step 1: Create individual segment files
            const segmentPaths = [];
            const fadeTime = autoMixPreset.fadeTime / 1000; // Convert to seconds
            const volumeReduction = autoMixPreset.gainReduction;
            
            updateProgress(85, m.audio_description_export_status_creating_segments());
            
            for (let i = 0; i < segments.length; i++) {
                const segment = segments[i];
                const segmentPath = tempProgramAudioPath.replace('.temp.', `.segment${i}.temp.`);
                segmentPaths.push(segmentPath);
                
                await new Promise((resolve, reject) => {
                    let ffmpegCmd = ffmpeg();
                    
                    // Apply audio codec settings
                    ffmpegCmd.audioCodec(audioCodecOptions.codec);
                    ffmpegCmd.audioChannels(audioCodecOptions.channels);
                    
                    if (audioCodecOptions.format === "mp3" && audioCodecOptions.bitrate) {
                        ffmpegCmd.audioBitrate(audioCodecOptions.bitrate);
                    } else if (audioCodecOptions.samples) {
                        ffmpegCmd.audioFrequency(audioCodecOptions.samples);
                    }
                    
                    const segmentDuration = segment.end - segment.start;
                    const crossfadeDuration = 0.2; // 200ms fade
                    
                    // Extract segment with timing
                    ffmpegCmd
                        .addInput(programAudioPath)
                        .inputOptions([`-ss ${segment.start.toFixed(3)}`, `-t ${segmentDuration.toFixed(3)}`]);
                    
                    // Apply processing based on segment type
                    if (segment.type === 'reduced') {
                        // AD segment: just apply volume reduction, no fades
                        ffmpegCmd.audioFilters(`volume=${volumeReduction}`);
                    } else {
                        // Normal segment: apply fades and determine which edges need fading
                        const filters = [];
                        
                        // Check if this segment needs fade-in (not the first segment)
                        const needsFadeIn = i > 0;
                        
                        // Check if this segment needs fade-out (not the last segment)
                        const needsFadeOut = i < segments.length - 1;
                        
                        // Add fade-in if needed
                        if (needsFadeIn && segmentDuration > crossfadeDuration) {
                            filters.push(`afade=t=in:d=${crossfadeDuration.toFixed(3)}:silence=${volumeReduction}`);
                        }
                        
                        // Add fade-out if needed
                        if (needsFadeOut && segmentDuration > crossfadeDuration) {
                            const fadeOutStart = segmentDuration - crossfadeDuration;
                            filters.push(`afade=t=out:st=${fadeOutStart.toFixed(3)}:d=${crossfadeDuration.toFixed(3)}:silence=${volumeReduction}`);
                        }
                        
                        // Apply filters if any exist
                        if (filters.length > 0) {
                            ffmpegCmd.audioFilters(filters.join(','));
                        }
                    }
                    
                    ffmpegCmd
                        .output(segmentPath)
                        .on("start", function (commandLine) {
                            console.log(`Creating segment ${i}: ${commandLine}`);
                            updateProgress(
                                85 + (i / segments.length) * 3,
                                m.audio_description_export_status_creating_segment({
                                    current: i + 1,
                                    total: segments.length
                                }),
                            );
                        })
                        .on("end", function () {
                            resolve();
                        })
                        .on("error", function (err, stdout, stderr) {
                            console.error(`Error creating segment ${i}:`, err.message);
                            console.error(stderr);
                            reject(err);
                        })
                        .run();
                });
            }
            
            // Step 2: Concatenate segments using acrossfade if multiple segments exist
            updateProgress(88, m.audio_description_export_status_combining_segments());
            
            if (segments.length === 1) {
                // Only one segment, just rename it
                await fs.rename(segmentPaths[0], tempProgramAudioPath);
            } else {
                // Multiple segments, use concat with crossfade
                await new Promise((resolve, reject) => {
                    let ffmpegCmd = ffmpeg();
                    
                    // Apply audio codec settings
                    ffmpegCmd.audioCodec(audioCodecOptions.codec);
                    ffmpegCmd.audioChannels(audioCodecOptions.channels);
                    
                    if (audioCodecOptions.format === "mp3" && audioCodecOptions.bitrate) {
                        ffmpegCmd.audioBitrate(audioCodecOptions.bitrate);
                    } else if (audioCodecOptions.samples) {
                        ffmpegCmd.audioFrequency(audioCodecOptions.samples);
                    }
                    
                    // Add all segment inputs
                    segmentPaths.forEach(segmentPath => {
                        ffmpegCmd.addInput(segmentPath);
                    });
                    
                    // Use simple concat filter instead of acrossfade for reliability
                    const concatInputs = segmentPaths.map((_, index) => `[${index}:a]`).join('');
                    const filterComplex = `${concatInputs}concat=n=${segments.length}:v=0:a=1[master]`;
                    
                    console.log("Concat filter complex:", filterComplex);
                    
                    ffmpegCmd
                        .complexFilter(filterComplex)
                        .outputOptions('-map [master]')
                        .output(tempProgramAudioPath)
                        .on("start", function (commandLine) {
                            console.log("Combining segments: " + commandLine);
                            updateProgress(90, m.audio_description_export_status_combining_segments());
                        })
                        .on("progress", function (progress) {
                            updateProgress(
                                91,
                                m.audio_description_export_status_combining_segments_progress({
                                    timecode: progress.timemark
                                }),
                            );
                        })
                        .on("end", function () {
                            resolve();
                        })
                        .on("error", function (err, stdout, stderr) {
                            console.error("Error combining segments:", err.message);
                            console.error(stderr);
                            reject(err);
                        })
                        .run();
                });
                
                // Clean up segment files
                for (const segmentPath of segmentPaths) {
                    try {
                        await fs.unlink(segmentPath);
                    } catch (err) {
                        console.error("Error deleting segment file:", segmentPath, err.message);
                    }
                }
            }
            
            // Step 3: Mix the processed program audio with the VO track
            await new Promise((resolve, reject) => {
                let ffmpegCmd = ffmpeg();
                
                // Apply audio codec settings for the final output
                ffmpegCmd.audioCodec(audioCodecOptions.codec);
                ffmpegCmd.audioChannels(audioCodecOptions.channels);
                
                if (audioCodecOptions.format === "mp3" && audioCodecOptions.bitrate) {
                    ffmpegCmd.audioBitrate(audioCodecOptions.bitrate);
                } else if (audioCodecOptions.samples) {
                    ffmpegCmd.audioFrequency(audioCodecOptions.samples);
                }
                
                // Simple mix of the processed program audio and VO
                ffmpegCmd
                    .addInput(tempProgramAudioPath)
                    .addInput(voAudioPathFinal)
                    .complexFilter("[0:a][1:a]amix=inputs=2:normalize=disabled[master]")
                    .outputOptions(`-map [master]`)
                    .output(tempMixdownPath)
                    .on("start", function (commandLine) {
                        console.log("Spawned Ffmpeg for mixing: " + commandLine);
                        updateProgress(92, m.audio_description_export_status_mixing_program_vo());
                    })
                    .on("progress", function (progress) {
                        console.log("Mixing audio:", progress.timemark);
                        updateProgress(
                            94,
                            m.audio_description_export_status_mixing_audio_progress({
                                timecode: progress.timemark
                            }),
                        );
                    })
                    .on("end", function () {
                        resolve();
                    })
                    .on("error", function (err, stdout, stderr) {
                        console.error("Error mixing audio:", err.message);
                        console.error(stderr);
                        console.error(stdout);
                        reject(err);
                    })
                    .run();
            });
            
            // Clean up the temporary program audio file
            try {
                await fs.unlink(tempProgramAudioPath);
            } catch (err) {
                console.error("Error deleting temporary program audio file:", err.message);
            }
            
        } else {
            // Standard single-pass approach for compression presets or extended AD
            await new Promise((resolve, reject) => {
                let ffmpegCmd = ffmpeg();
                ffmpegCmd.audioCodec(audioCodecOptions.codec);
                ffmpegCmd.audioChannels(audioCodecOptions.channels);

                if (
                    audioCodecOptions.format === "mp3" &&
                    audioCodecOptions.bitrate
                ) {
                    ffmpegCmd.audioBitrate(audioCodecOptions.bitrate);
                } else if (audioCodecOptions.samples) {
                    ffmpegCmd.audioFrequency(audioCodecOptions.samples);
                }

                let filterComplex;

            if (exportExtendedAD) {
                // Create a filter that cuts and shifts program audio around AD events
                let filterSegments = []; // Calculate the original media duration (before extension)
                let filterStr = ``;
                const originalDuration = duration - totalADDuration; // Build segments between AD events
                let lastOriginalEnd = 0;
                let currentPgmTime = 0;

                extendedADEvents.forEach((event, evIndex, evs) => {
                    if (event.originalStart > currentPgmTime){
                        filterSegments.push({
                            id : `p${evIndex}`,
                            start : currentPgmTime,
                            end : event.originalStart,
                            type : "program"
                        });

                        currentPgmTime = event.originalStart;
                    }
                    
                    filterSegments.push({
                        id : `s${evIndex}`,
                        start : event.start,
                        end : event.end,
                        type : "silence"
                    });
                });

                filterSegments.push({
                    id : `p${extendedADEvents.length}`,
                    start : currentPgmTime,
                    type : "program"
                });


                const segmentLabels = [];
                filterSegments.forEach((segment, index) => {
                    console.log(segment);
                    if (segment.type === "program"){
                        if (segment.end){
                            filterStr += `[0:a]atrim=${segment.start.toFixed(3)}:${segment.end.toFixed(3)}[${segment.id}];`;
                            segmentLabels.push(`[${segment.id}]`);
                        } else {
                            filterStr += `[0:a]atrim=${segment.start.toFixed(3)}[${segment.id}];`;
                            segmentLabels.push(`[${segment.id}]`);
                        }
                    } else if (segment.type === "silence"){
                        filterStr += `anullsrc=r=${audioPreset.samples}:cl=stereo,atrim=0:${(segment.end - segment.start).toFixed(3)}[${segment.id}];`;
                        segmentLabels.push(`[${segment.id}]`);
                    }                    
                });

                filterComplex = filterStr + segmentLabels.join('') + `concat=n=${filterSegments.length}:v=0:a=1[program];[program][1:a]amix=inputs=2:normalize=disabled[master]`;

                console.log("Extended AD filter complex:", filterComplex);
            } else if (autoMixPreset.type === "none") {
                filterComplex = `[0:a][1:a]amix=inputs=2:normalize=disabled[master]`;
            } else {
                // Original sidechain compression for standard AD
                filterComplex = `[1:a]asplit=2[sc][mix];[0:a][sc]sidechaincompress=ratio=${autoMixPreset.ratio}:threshold=${autoMixPreset.threshold}:release=${autoMixPreset.release}:attack=${autoMixPreset.attack}:knee=${autoMixPreset.knee}[compr];[compr][mix]amix=inputs=2:normalize=disabled[master]`;
            }

            ffmpegCmd
                .addInput(programAudioPath)
                .addInput(voAudioPathFinal)
                .complexFilter(filterComplex)
                .outputOptions(`-map [master]`)
                .output(tempMixdownPath)
                .on("start", function (commandLine) {
                    console.log("Spawned Ffmpeg with command: " + commandLine);

                    updateProgress(
                        88,
                        exportExtendedAD
                            ? m.audio_description_export_status_creating_mixdown_extended()
                            : m.audio_description_export_status_creating_mixdown(),
                    );
                })
                .on("progress", function (progress) {
                    console.log("Exporting", progress.timemark);
                    updateProgress(
                        90,
                        m.audio_description_export_status_mixdown_progress({ timecode: progress.timemark }),
                    );
                })
                .on("end", function () {
                    resolve();
                })
                .on("error", function (err, stdout, stderr) {
                    console.error("Error creating mixdown:", err.message);
                    console.error(stderr);
                    console.error(stdout);
                    reject(err);
                })
                .run();
            });
        } // End of the if-else for processing approach

        // STEP 2: APPLY LOUDNESS NORMALIZATION (same for all preset types)
        if (loudnessMixdown && loudnessEnabled) {
            // Apply two-pass loudness normalization
            await applyLoudnessNormalization(
                tempMixdownPath,
                mixdownAudioPath,
                audioCodecOptions,
                loudnessPreset.target,
                loudnessPreset.range,
                loudnessPreset.peak,
                (percent, msg) => {
                    // Map progress from 0-100 to 90-99
                    const mappedPercent = 90 + percent * 0.09;
                    updateProgress(mappedPercent, msg);
                },
            ); // Delete the temp file

            await fs.unlink(tempMixdownPath);
        } else {
            // Just rename the temp file to the final path
            await fs.rename(tempMixdownPath, mixdownAudioPath);
        }

        updateProgress(100, m.audio_description_export_status_mixdown_complete());
        console.log("Finished Exporting");
        toast.push(m.audio_description_export_complete_toast(), {
            classes: ["toast-success"],
        }); // Embed BWF metadata for WAV files if enabled

        if (extension === "wav" && bwfMetadata.enable) {
            embedBwfMetadata(mixdownAudioPath);
        }

        if (!keepVoTracks) {
            for (let voTrack = 0; voTrack < voAudioFiles.length; voTrack++) {
                try {
                    fs.unlink(
                        voAudioFiles[voTrack].audioFilePath,
                        (err) => {
                            if (err) {
                                console.error(err);
                            }
                        },
                    );
                } catch (err) {
                    console.log(
                        "Error deleting vo track file",
                        voAudioFiles[voTrack],
                    );
                    console.log(err.message);
                }
            }
        }

        // Export video if enabled, otherwise finish
        if (exportVideo) {
            await exportVideoFile();
        }

        endExportProcess();
    } catch (err) {
        console.log(err, err.message);
        toast.push(m.audio_description_export_failed_generic(), {
            classes: ["toast-danger"]
        });
        cancelJobWithError(err.message);
    }
}

/**
 * Exports video with audio description mixed in
 * For standard AD: replaces audio track
 * For Extended AD: adds frame holds to match extended timing
 */
async function exportVideoFile() {
    try {
        if (!customVideoPath) {
            console.error("No video file selected for export");
            toast.push(m.audio_description_export_video_missing(), { classes: ["toast-danger"] });
            return;
        }

        updateProgress(92, m.audio_description_export_status_preparing_video());

        if (exportExtendedAD) {
            // Extended AD: Create video with frame holds
            await exportExtendedADVideo();
        } else {
            // Standard AD: Simple audio replacement
            await exportStandardADVideo();
        }

        updateProgress(100, m.audio_description_export_status_video_complete());
        toast.push(m.audio_description_export_video_complete_toast(), {
            classes: ["toast-success"],
        });

    } catch (err) {
        console.error("Error exporting video:", err);
        toast.push(m.audio_description_export_video_failed({ error: err.message }), {
            classes: ["toast-danger"],
        });
        throw err;
    }
}

/**
 * Exports standard AD video by replacing audio track
 */
async function exportStandardADVideo() {
    return new Promise((resolve, reject) => {
        updateProgress(93, m.audio_description_export_status_video_encoding_standard());

        let ffmpegCmd = ffmpeg();

        // Add inputs: video file and mixdown audio
        ffmpegCmd.addInput(customVideoPath);
        ffmpegCmd.addInput(mixdownAudioPath);

        // Hardware acceleration
        ffmpegCmd.inputOptions('-hwaccel auto');

        // Map video from first input (copy, no re-encoding)
        ffmpegCmd.outputOptions('-map 0:v');
        
        // Map audio from second input
        ffmpegCmd.outputOptions('-map 1:a');

        // Video codec: copy (no re-encoding for speed)
        ffmpegCmd.videoCodec('copy');

        // Audio codec: AAC for MP4 compatibility
        ffmpegCmd.audioCodec('aac');
        ffmpegCmd.audioBitrate('192k');
        ffmpegCmd.audioChannels(2);

        // Output
        ffmpegCmd
            .output(videoOutputPath)
            .on('start', function (commandLine) {
                console.log('Video export FFmpeg command:', commandLine);
            })
            .on('progress', function (progress) {
                console.log('Video export progress:', progress.timemark);
                if (progress.timemark) {
                    updateProgress(
                        95,
                        m.audio_description_export_status_video_encoding_progress({ timecode: progress.timemark })
                    );
                }
            })
            .on('error', function (err) {
                console.error('Error exporting video:', err);
                reject(err);
            })
            .on('end', function () {
                console.log('Video export complete');
                resolve();
            })
            .run();
    });
}

/**
 * Exports Extended AD video with frame holds
 * Creates freeze frames at AD insertion points
 */
async function exportExtendedADVideo() {
    return new Promise((resolve, reject) => {
        updateProgress(93, m.audio_description_export_status_video_encoding_extended());

        let ffmpegCmd = ffmpeg();

        // Add inputs: video file and extended mixdown audio
        ffmpegCmd.addInput(customVideoPath);
        ffmpegCmd.addInput(mixdownAudioPath);

        // Hardware acceleration
        ffmpegCmd.inputOptions('-hwaccel auto');

        // Build loop filter for frame holds at AD insertion points
        // Using loop filter: loop=loop=<count>:size=1:start=<frame_number>
        const frameRate = projectState.frameRate;
        let loopFilters = [];
        let offset = 0;
        extendedADEvents.forEach((event) => {
            // Calculate the frame number where the AD should be inserted
            const adInsertionPoint = event.originalStart;
            const adDuration = event.end - event.start;
            
            // Convert time to frame number
            const startFrame = Math.floor(adInsertionPoint * frameRate) + Math.floor(offset * frameRate);
            offset += adDuration;
            
            // Calculate how many times to loop (number of frames to add)
            // Subtract 1 because the original frame is already there
            const loopCount = Math.floor(adDuration * frameRate) - 1;
            
            if (loopCount > 0) {
                loopFilters.push(`loop=loop=${loopCount}:size=1:start=${startFrame}`);
            }
        });

        // Build the complete video filter with all loop filters chained
        const videoFilter = loopFilters.length > 0 ? loopFilters.join(',') : null;

        console.log('Extended AD video filter:', videoFilter);

        // Apply video filter if we have loop operations
        if (videoFilter) {
            ffmpegCmd.videoFilters(videoFilter);
        }

        // Map outputs: video and extended audio
        ffmpegCmd.outputOptions('-map 0:v');
        ffmpegCmd.outputOptions('-map 1:a');

        // Video codec: H.264 with sensible defaults
        ffmpegCmd.videoCodec('libx264');
        ffmpegCmd.outputOptions([
            '-preset medium',
            '-crf 23',
            '-pix_fmt yuv420p'
        ]);

        // Audio codec: AAC for MP4 compatibility
        ffmpegCmd.audioCodec('aac');
        ffmpegCmd.audioBitrate('192k');
        ffmpegCmd.audioChannels(2);

        // Output
        ffmpegCmd
            .output(videoOutputPath)
            .on('start', function (commandLine) {
                console.log('Extended AD video FFmpeg command:', commandLine);
            })
            .on('progress', function (progress) {
                console.log('Extended AD video progress:', progress.percent);
                if (progress.percent) {
                    updateProgress(
                        93 + (progress.percent / 100) * 6,
                        m.audio_description_export_status_video_encoding_extended_progress({
                            percent: progress.percent.toFixed(1)
                        })
                    );
                }
            })
            .on('error', function (err) {
                console.error('Error exporting extended AD video:', err);
                reject(err);
            })
            .on('end', function () {
                console.log('Extended AD video export complete');
                resolve();
            })
            .run();
    });
}

/**
 * Completes the export process, shows exported files, and saves settings
 */
function endExportProcess() {
    exporting = false;
    // Show the appropriate file in folder
    if (exportVideo && videoOutputPath) {
        shell.showItemInFolder(videoOutputPath);
    } else {
        shell.showItemInFolder(mixdownAudioPath);
    }
    localStorage.setItem(
        settingsStorageKey,
        JSON.stringify({
            autoMixPresetName: autoMixPreset.displayName,
            keepVoTracks: keepVoTracks,
            exportMixdown: exportMixdown,
            exportSrtFile: exportSrtFile,
            exportExtendedAD: exportExtendedAD,
            exportVideo: exportVideo,
            controlTrackLayout: controlTrackLayout,
            controlTrackFadeDb: controlTrackFadeDb,
            controlTrackFadeInMs: controlTrackFadeInMs,
            controlTrackFadeOutMs: controlTrackFadeOutMs,
            // Save type-specific properties
            type: autoMixPreset.type,
            // Compression properties
            ratio: autoMixPreset.ratio,
            threshold: autoMixPreset.threshold,
            release: autoMixPreset.release,
            attack: autoMixPreset.attack,
            knee: autoMixPreset.knee,
            mix: autoMixPreset.mix,
            // Fixed gain properties
            gainReduction: autoMixPreset.gainReduction,
            fadeTime: autoMixPreset.fadeTime,
            // Other settings
            loudnessPreset: loudnessPreset,
            audioPreset: audioPreset,
            loudnessVo: loudnessVo,
            loudnessProgram: loudnessProgram,
            loudnessMixdown: loudnessMixdown,
        }),
    );
    hideModal();
}

/**
 * Embeds Broadcast Wave Format metadata into WAV file
 * @param {string} filePath - Path to WAV file
 * @param {number} [sampleRate] - Sample rate of the file
 */
function embedBwfMetadata(filePath, sampleRate = audioPreset.samples) {
    try {
        let wav = new WaveFile(fsSync.readFileSync(filePath));
        /* wav.bext = {
            description: bwfMetadata.description,
            originator: "Closed Caption Creator",
            originatorReference: jobInfo.id,
            originationDate: new Date().toISOString().split('T')[0],
            originationTime: new Date().toISOString().split('T')[1].split('.')[0],
            timeReference: tcLib.tcToSec(bwfMetadata.referenceTime, projectState.frameRate, projectState.dropFrame) * audioPreset.samples,
            version: 1,
            UMID: "",
            codingHistory: ""
        }; */

        wav.bext.originator = "Closed Caption Creator";
        wav.bext.description =
            bwfMetadata.description || "Created by Closed Caption Creator";
        try {
            if (bwfMetadata.referenceTime) {
                let incodeSec = tcLib.tcToSec(
                    bwfMetadata.referenceTime,
                    projectState.frameRate,
                    projectState.dropFrame,
                );
                wav.bext.timeReference = secondsToBWFTimeReference(
                    incodeSec,
                    sampleRate,
                );
            } else {
                wav.bext.timeReference = [0, 0];
            }
        } catch (err) {
            console.log("Error setting time reference in BWF metadata");
            console.log(err.message);
            wav.bext.timeReference = [0, 0];
        }

        fsSync.writeFileSync(filePath, wav.toBuffer());
        console.log("BWF Metadata embedded successfully.");
        return;
    } catch (err) {
        console.error("Error embedding BWF Metadata: ", err.message);
    }
}

/**
 * Converts seconds to BWF time reference format
 * @param {number} seconds - Time in seconds
 * @param {number} sampleRate - Sample rate of audio file
 * @returns {Array} - [low32bits, high32bits] of sample count
 */
function secondsToBWFTimeReference(seconds, sampleRate) {
    // Convert seconds to samples
    const totalSamples = Math.floor(seconds * sampleRate);

    // Convert to BigInt to handle 64-bit arithmetic
    const samplesBI = BigInt(totalSamples);

    // Create a mask for 32 bits
    const mask32 = BigInt(0xffffffff);

    // Get high and low 32-bit values
    const timeReferenceLow = Number(samplesBI & mask32);
    const timeReferenceHigh = Number(samplesBI >> BigInt(32));

    return [timeReferenceLow, timeReferenceHigh];
}

/**
 * Exports SRT subtitles from event group data
 * @param {Object} evg - Event group data
 * @param {boolean} exportEventSrt - Whether to export individual event SRTs
 * @param {string} fileBasePath - Base file path for output
 */
async function exportSrt(evg, exportEventSrt, fileBasePath) {
    evg = JSON.parse(JSON.stringify(evg));

    /* Selective export */
    if (selection) {
        evg.events = evg.events.filter((event, index) => {
            return index + 1 >= selectionStart && index + 1 <= selectionEnd;
        });
    }

    let options = new defaults.options({
        profile: "subRip",
        frameRate: projectState.frameRate,
        dropFrame: projectState.dropFrame,
        target_profile: "subRip",
        source_profile: "closedCaptionProject",
        incode: "00:00:00:00",
    });

    let eventGroupsToExport = [];
    if (exportMixdown) {
        eventGroupsToExport.push(evg);
    }

    if (exportEventSrt) {
        evg.events.forEach((evt) => {
            let evgNew = {
                ...new _EventGroup({
                    ...copyOfEventGroup,
                    events: [
                        {
                            ...evt,
                            start: 0,
                            end: evt.end - evt.start,
                        },
                    ],
                }),
            };

            eventGroupsToExport.push(evgNew);
        });
    }

    for (let i = 0; i < eventGroupsToExport.length; i++) {
        let output;
        let srtFilePath = fileBasePath + "_track" + i + ".srt";

        if (exportEventSrt && exportMixdown && i > 0) {
            eventGroupsToExport[i] = autoFormat(
                eventGroupsToExport[i],
                maxLines,
                maxChars,
                0.83,
                9999,
            );
        } else if (exportEventSrt) {
            eventGroupsToExport[i] = autoFormat(
                eventGroupsToExport[i],
                maxLines,
                maxChars,
                0.83,
                9999,
            );
        }

        if (exportMixdown && i === 0) {
            srtFilePath = fileBasePath + "_mixdown.srt";
        }

        output = await encode(eventGroupsToExport[i], options);
        await fs.writeFile(srtFilePath, output);
    }
}

/**
 * Downloads a file from URL and saves to local filesystem
 * @param {string} fileUrl - URL of file to download
 * @param {string} filePath - Local path to save file
 * @returns {Promise<string>} - Path to downloaded file
 */
async function writeFileStream(fileUrl, filePath) {
    try {
        console.info("Downloading audio file from", fileUrl, "to", filePath);
        await ipcRenderer.invoke("downloadRequest", {
            fileUrl: fileUrl,
            filePath: filePath,
        });

        return filePath;
    } catch (err) {
        console.log(err);
        console.log(err.message);
        throw new Error(m.audio_description_export_audio_stream_failed());
    }
}

/* Pre-Checks */
/**
 * Shows trial mode warning and option to activate account
 */
function showTrialWarning() {
    Swal.fire({
        ...SWAL_BASE,
        titleText: m.audio_description_export_trial_title(),
        text: m.audio_description_export_trial_text(),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.audio_description_export_trial_activate(),
        denyButtonText: m.audio_description_export_trial_continue(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.trial,
    })
        .then((result) => {
            if (result.isConfirmed) {
                activateSubscription();
            }
        })
        .catch((err) => {
            console.log(err);
            console.log(err.message);
        });
}

/**
 * Cancels the export and highlights the problematic event
 * @param {number} eventGroupId - Event group index
 * @param {number} eventId - Event index to highlight
 */
async function cancelExport(eventGroupId, eventId) {
    toast.push(m.audio_description_export_aborted_toast(), { classes: ["toast-warning"] });

    projectState.selected = eventGroupId;
    eventGroupState[projectState.selected].selected = [eventId];
    setTimeout(() => {
        const selectedIndex =
            eventGroupState[projectState.selected].selected[0];

        // Dispatch custom event to scroll to the selected event
        window.dispatchEvent(
            new CustomEvent("scrollToVirtualListItem", {
                detail: { index: selectedIndex },
            }),
        );

        if (lockState.video && player) {
            player.currentTime =
                eventGroupState[projectState.selected].events[
                    selectedIndex
                ].start;
        }
    }, 250);

    hideModal();
}

/**
 * Displays a warning dialog to the user with options to abort, ignore, or ignore all
 * @param {string} msg - Message to display
 * @returns {Promise<boolean>} - True if user chose to abort
 */
function alertUser(msg) {
    let response = Swal.fire({
        ...SWAL_BASE,
        titleText: m.audio_description_export_precheck_title(),
        text: msg,
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: m.audio_description_export_precheck_abort(),
        denyButtonText: m.audio_description_export_precheck_ignore(),
        cancelButtonText: m.audio_description_export_precheck_ignore_all(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.precheck,
    }).then((result) => {
        if (result.isConfirmed) {
            return true;
        } else if (result.isDenied) {
            return false;
        } else if (result.isDismissed) {
            ignoreAll = true;
            return false;
        }
    });

    return response;
}

/**
 * Validates events before export, checking for common issues
 * @param {Array} events - Array of events to check
 * @returns {Object} - Result with err flag and eventId if error
 */
async function preExportCheck(events) {
    for (let i = 0; i < events.length; i++) {
        if (!ignoreAll) {
            if (events[i].text) {
                if (isNaN(events[i].start)) {
                    if (
                        await alertUser(
                            m.audio_description_export_event_missing_start({ index: i + 1 }),
                        )
                    ) {
                        return {
                            err: true,
                            eventId: i,
                        };
                    }
                } else if (isNaN(events[i].end)) {
                    if (
                        await alertUser(
                            m.audio_description_export_event_missing_end({ index: i + 1 }),
                        )
                    ) {
                        return {
                            err: true,
                            eventId: i,
                        };
                    }
                } else if (events[i].start === events[i].end) {
                    if (
                        await alertUser(
                            m.audio_description_export_event_same_time({ index: i + 1 }),
                        )
                    ) {
                        return {
                            err: true,
                            eventId: i,
                        };
                    }
                } else if (events[i].start > events[i].end) {
                    if (
                        await alertUser(
                            m.audio_description_export_event_start_after_end({ index: i + 1 }),
                        )
                    ) {
                        return {
                            err: true,
                            eventId: i,
                        };
                    }
                } else if (i > 0 && events[i].start < events[i - 1].end) {
                    /* Adding check in case overlap is less than a frame */
                    if (
                        events[i - 1].end - events[i].start <
                        1 / projectState.frameRate
                    ) {
                        events[i].start = events[i - 1].end;
                    } else if (
                        await alertUser(
                            m.audio_description_export_event_overlap({ index: i + 1 }),
                        )
                    ) {
                        return {
                            err: true,
                            eventId: i,
                        };
                    }
                }

                if (eventNeedsRender(
                    eventGroupState[eventGroupIndex]?.events?.find((live) => live.id === events[i].id) || events[i]
                )) {
                    if (
                        await alertUser(
                            m.audio_description_export_event_needs_render({ index: i + 1 }),
                        )
                    ) {
                        return {
                            err: true,
                            eventId: i,
                        };
                    }
                }
            } else {
                if (
                    await alertUser(
                        m.audio_description_export_event_missing_text({ index: i + 1 }),
                    )
                ) {
                    return {
                        err: true,
                        eventId: i,
                    };
                }
            }
        }
    }

    return {
        err: false,
    };
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === modalKey
        ? 'show d-block'
        : ''}"
    role="dialog"
    tabindex="-1"
    id={isDubbing ? "AudioDubbingExportModal" : "AudioDescriptionExportModal"}
    aria-labelledby="audioDescriptionExportTitle"
    aria-describedby="audioDescriptionExportDesc">
    <div
        class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
        role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 id="audioDescriptionExportTitle" class="modal-title">
                    {#if isDubbing}
                        <i class="bi bi-soundwave"></i> {m.audio_dubbing_export_title()}
                    {:else}
                        <i class="bi bi-badge-ad-fill"></i> {m.audio_description_export_title()}
                    {/if}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.audio_description_export_close_label()}
                    onclick={() => hideModal()}
                    id="closeAudioDescriptionExport"></button>
            </div>
            <div class="modal-body" id="audioDescriptionExportDesc">
                {#if exporting}
                    <div class="alert alert-warning d-flex align-items-center" transition:fade={{ duration: 200 }}>
                        <i class="bi bi-exclamation-diamond-fill me-2"></i>
                        <span>{m.audio_description_export_keep_open_warning()}</span>
                    </div>
                {/if}
                <form onsubmit={preventDefault(beginExport)}>
                    <!-- Export Settings Card (Always Visible) -->
                    <div class="card mb-3 border-primary">
                        <div class="card-header bg-primary text-white">
                            <h5 class="mb-0">
                                <i class="bi bi-gear-fill"></i> {m.audio_description_export_settings_title()}
                            </h5>
                        </div>
                        <div class="card-body">
                            <div class="mb-3">
                                <label class="form-label fw-bold" for="targetFolderInputGroup">
                                    <i class="bi bi-folder"></i> {m.audio_description_export_folder_label()}
                                </label>
                                <div class="input-group">
                                    <button
                                        type="button"
                                        class="btn btn-outline-secondary text-dark"
                                        onclick={selectTargetFolder}
                                        disabled={exporting}>
                                        <i class="bi bi-folder-open"></i> {m.audio_description_export_select_folder_button()}
                                    </button>
                                    <input
                                        type="text"
                                        class="form-control"
                                        value={targetFolderPath || ""}
                                        placeholder={m.audio_description_export_no_folder_selected()}
                                        readonly />
                                </div>
                                {#if !targetFolderPath}
                                    <small class="form-text text-muted">
                                        <i class="bi bi-info-circle"></i> {m.audio_description_export_folder_help()}
                                    </small>
                                {/if}
                            </div>
                            <div class="mb-3">
                                <label class="form-label fw-bold" for="EventGroupSelection">
                                    <i class="bi bi-collection"></i> {m.audio_description_export_event_group_label()}
                                </label>
                                <select
                                    class="form-select"
                                    bind:value={eventGroupId}
                                    disabled={exporting}>
                                    {#each eventGroupState.filter((group) => {
                                        return group.type === groupType;
                                    }) as eventGroupOption (eventGroupOption.id)}
                                        <option
                                            value={eventGroupOption.id}
                                            selected={eventGroupId ===
                                                eventGroupOption.id}
                                            >{eventGroupOption.name}</option>
                                    {/each}
                                </select>
                            </div>
                            <div class="mb-0">
                                <label class="form-label fw-bold" for="AudioPreset">
                                    <i class="bi bi-music-note-beamed"></i> {m.audio_description_export_audio_format_label()}
                                </label>
                                <select
                                    class="form-select"
                                    bind:value={audioPreset}
                                    disabled={exporting}>
                                    {#each audioExportProfiles as audioProfile (audioProfile.displayName ?? audioProfile.format)}
                                        <option value={audioProfile}
                                            >{audioProfile.displayName}</option>
                                    {/each}
                                </select>
                            </div>
                        </div>
                    </div>
                    
                    <div class="card mb-3">
                        <div class="card-header bg-light" role="button" tabindex="0" onclick={() => showOutputOptions = !showOutputOptions} onkeydown={(e) => e.key === 'Enter' && (showOutputOptions = !showOutputOptions)}>
                            <div class="d-flex justify-content-between align-items-center">
                                <h5 class="mb-0">
                                    <i class="bi bi-check2-square"></i> {m.audio_description_export_output_options_title()}
                                </h5>
                                <i class="bi bi-chevron-{showOutputOptions ? 'up' : 'down'}"></i>
                            </div>
                        </div>
                        {#if showOutputOptions}
                            <div class="card-body" transition:fade={{ duration: 200 }}>
                                <div class="row g-3">
                                    <div class="col-md-6">
                                        <div class="form-check form-switch">
                                            <input
                                                class="form-check-input"
                                                type="checkbox"
                                                id="keepVoTracksToggle"
                                                bind:checked={keepVoTracks} 
                                                disabled={exporting} />
                                            <label
                                                class="form-check-label"
                                                for="keepVoTracksToggle">
                                                <i class="bi bi-file-music"></i> {m.audio_description_export_keep_vo_tracks()}
                                            </label>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-check form-switch">
                                            <input
                                                class="form-check-input"
                                                type="checkbox"
                                                id="exportMixdownToggle"
                                                bind:checked={exportMixdown} 
                                                disabled={exporting} />
                                            <label
                                                class="form-check-label"
                                                for="exportMixdownToggle">
                                                <i class="bi bi-soundwave"></i> {m.audio_description_export_mixdown_label()}
                                            </label>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-check form-switch">
                                            <input
                                                class="form-check-input"
                                                type="checkbox"
                                                id="exportSrtFileToggle"
                                                bind:checked={exportSrtFile} 
                                                disabled={exporting} />
                                            <label
                                                class="form-check-label"
                                                for="exportSrtFileToggle">
                                                <i class="bi bi-file-earmark-text"></i> {m.audio_description_export_srt_label()}
                                            </label>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-check form-switch">
                                            <input
                                                class="form-check-input"
                                                type="checkbox"
                                                id="exportVideoToggle"
                                                bind:checked={exportVideo}
                                                disabled={exporting || !exportMixdown} />
                                            <label
                                                class="form-check-label"
                                                for="exportVideoToggle"
                                                title={!exportMixdown ? m.audio_description_export_video_requires_mixdown() : ""}>
                                                <i class="bi bi-film"></i> {isDubbing ? m.audio_dubbing_export_video_label() : m.audio_description_export_video_label()}
                                            </label>
                                            {#if exportVideo && !exportMixdown}
                                                <small class="text-muted d-block">
                                                    <i class="bi bi-info-circle"></i> {m.audio_description_export_video_requires_mixdown_short()}
                                                </small>
                                            {/if}
                                        </div>
                                    </div>
                                </div>
                                {#if exportMixdown}
                                    <div class="mt-3" transition:fade={{ duration: 200 }}>
                                        <label for="programAudioFile" class="form-label fw-bold">
                                            {#if isDubbing}
                                                <i class="bi bi-layers"></i> {m.audio_dubbing_export_background_stem_label()}
                                            {:else}
                                                <i class="bi bi-music-note"></i> {m.audio_description_export_program_audio_label()}
                                            {/if}
                                        </label>
                                        <div class="input-group">
                                            <input
                                                type="file"
                                                class="form-control"
                                                id="programAudioFile"
                                                accept="audio/*,video/*"
                                                onchange={handleFileSelection}
                                                disabled={exporting} />
                                            {#if customAudioPath}
                                                <button
                                                    aria-label={m.audio_description_export_remove_audio_file_label()}
                                                    type="button"
                                                    class="btn btn-outline-danger"
                                                    transition:fade={{ duration: 150 }}
                                                    onclick={() => {
                                                        customAudioPath = null;
                                                        files = [];
                                                    }}
                                                    disabled={exporting}>
                                                    <i class="bi bi-x"></i>
                                                </button>
                                            {/if}
                                        </div>
                                        <small class="form-text text-muted">
                                            {#if customAudioPath && customAudioPath === backgroundStemPath}
                                                <i class="bi bi-layers text-success"></i> {m.audio_description_export_background_stem_label()} {customAudioPath
                                                    .split(/[\\/]/)
                                                    .pop()}
                                            {:else if customAudioPath}
                                                <i class="bi bi-check-circle text-success"></i> {m.audio_description_export_selected_file_label()} {customAudioPath
                                                    .split(/[\\/]/)
                                                    .pop()}
                                            {:else}
                                                <i class="bi bi-info-circle"></i> {isDubbing ? m.audio_dubbing_export_background_stem_help() : m.audio_description_export_program_audio_help()}
                                            {/if}
                                        </small>
                                    </div>
                                {/if}
                                {#if exportVideo}
                                    <div class="mt-3" transition:fade={{ duration: 200 }}>
                                        <label for="videoFile" class="form-label fw-bold">
                                            <i class="bi bi-film"></i> {m.audio_description_export_video_file_label()} <span class="badge bg-danger">{m.audio_description_export_required_badge()}</span>
                                        </label>
                                        <div class="input-group">
                                            <input
                                                type="file"
                                                class="form-control"
                                                id="videoFile"
                                                accept="video/*"
                                                onchange={handleVideoFileSelection}
                                                disabled={exporting} />
                                            {#if customVideoPath}
                                                <button
                                                    aria-label={m.audio_description_export_remove_video_file_label()}
                                                    type="button"
                                                    class="btn btn-outline-danger"
                                                    transition:fade={{ duration: 150 }}
                                                    onclick={() => {
                                                        customVideoPath = null;
                                                        videoFiles = [];
                                                    }}
                                                    disabled={exporting}>
                                                    <i class="bi bi-x"></i>
                                                </button>
                                            {/if}
                                        </div>
                                        <small class="form-text text-muted">
                                            {#if customVideoPath && customVideoPath === projectVideoPath}
                                                <i class="bi bi-film text-success"></i> {m.audio_dubbing_export_project_video_label()} {customVideoPath
                                                    .split(/[\\/]/)
                                                    .pop()}
                                            {:else if customVideoPath}
                                                <i class="bi bi-check-circle text-success"></i> {m.audio_description_export_selected_file_label()} {customVideoPath
                                                    .split(/[\\/]/)
                                                    .pop()}
                                            {:else}
                                                <i class="bi bi-info-circle"></i> {m.audio_description_export_video_file_help()}
                                            {/if}
                                        </small>
                                    </div>
                                {/if}
                                {#if exportSrtFile}
                                    <div class="mt-3 p-3 border rounded bg-light" transition:fade={{ duration: 200 }}>
                                        <h6 class="mb-3"><i class="bi bi-file-text"></i> {m.audio_description_export_subtitle_settings_title()}</h6>
                                        <div class="row g-3">
                                            <div class="col-md-6">
                                                <label class="form-label" for="maxLinesInput">{m.audio_description_export_subtitle_max_lines_label()}</label>
                                                <input
                                                    type="number"
                                                    class="form-control"
                                                    id="maxLinesInput"
                                                    bind:value={maxLines}
                                                    min="1"
                                                    max="4"
                                                    step="1"
                                                    disabled={exporting} />
                                                <small class="form-text text-muted">{m.audio_description_export_subtitle_max_lines_help()}</small>
                                            </div>
                                            <div class="col-md-6">
                                                <label class="form-label" for="maxCharsInput">{m.audio_description_export_subtitle_max_chars_label()}</label>
                                                <input
                                                    type="number"
                                                    class="form-control"
                                                    id="maxCharsInput"
                                                    bind:value={maxChars}
                                                    step="1"
                                                    disabled={exporting} />
                                                <small class="form-text text-muted">{m.audio_description_export_subtitle_max_chars_help()}</small>
                                            </div>
                                        </div>
                                    </div>
                                {/if}
                                {#if !isDubbing}
                                    <div class="mt-3">
                                        <label class="form-label fw-bold" for="controlTrackLayout">
                                            <i class="bi bi-broadcast"></i> {m.audio_description_export_control_track_label()}
                                        </label>
                                        <select
                                            id="controlTrackLayout"
                                            class="form-select"
                                            bind:value={controlTrackLayout}
                                            disabled={exporting}>
                                            {#each controlTrackLayouts as layout (layout.id)}
                                                <option value={layout.id}>{controlTrackLayoutLabels[layout.id]()}</option>
                                            {/each}
                                        </select>
                                        <small class="form-text text-muted">
                                            <i class="bi bi-info-circle"></i> {m.audio_description_export_control_track_help()}
                                        </small>
                                    </div>
                                    {#if controlTrackLayout !== "none"}
                                        <div class="mt-3 p-3 border rounded bg-light" transition:fade={{ duration: 200 }}>
                                            <div class="row g-3">
                                                <div class="col-md-4">
                                                    <label class="form-label" for="controlTrackFadeDb">{m.audio_description_export_control_track_fade_label()}</label>
                                                    <input
                                                        type="number"
                                                        class="form-control"
                                                        id="controlTrackFadeDb"
                                                        bind:value={controlTrackFadeDb}
                                                        min="-76.5"
                                                        max="0"
                                                        step="0.3"
                                                        disabled={exporting} />
                                                </div>
                                                <div class="col-md-4">
                                                    <label class="form-label" for="controlTrackFadeInMs">{m.audio_description_export_control_track_fade_in_label()}</label>
                                                    <input
                                                        type="number"
                                                        class="form-control"
                                                        id="controlTrackFadeInMs"
                                                        bind:value={controlTrackFadeInMs}
                                                        min="0"
                                                        max="5000"
                                                        step="100"
                                                        disabled={exporting} />
                                                </div>
                                                <div class="col-md-4">
                                                    <label class="form-label" for="controlTrackFadeOutMs">{m.audio_description_export_control_track_fade_out_label()}</label>
                                                    <input
                                                        type="number"
                                                        class="form-control"
                                                        id="controlTrackFadeOutMs"
                                                        bind:value={controlTrackFadeOutMs}
                                                        min="0"
                                                        max="5000"
                                                        step="100"
                                                        disabled={exporting} />
                                                </div>
                                            </div>
                                            <small class="form-text text-muted">{m.audio_description_export_control_track_fade_help()}</small>
                                        </div>
                                    {/if}
                                {/if}
                            </div>
                        {/if}
                    </div>

                    <!-- Audio Processing Card (Only shown when Mixdown is enabled) -->
                    {#if exportMixdown}
                        <div class="card mb-3" transition:fade={{ duration: 200 }}>
                            <div class="card-header bg-light" role="button" tabindex="0" onclick={() => showAudioProcessing = !showAudioProcessing} onkeydown={(e) => e.key === 'Enter' && (showAudioProcessing = !showAudioProcessing)}>
                                <div class="d-flex justify-content-between align-items-center">
                                    <h5 class="mb-0">
                                        <i class="bi bi-sliders"></i> {m.audio_description_export_audio_processing_title()}
                                    </h5>
                                    <i class="bi bi-chevron-{showAudioProcessing ? 'up' : 'down'}"></i>
                                </div>
                            </div>
                            {#if showAudioProcessing}
                                <div class="card-body" transition:fade={{ duration: 200 }}>
                                        <h6 class="mb-3"><i class="bi bi-shuffle"></i> {m.audio_description_export_automix_title()}</h6>
                                    <div class="mb-3">
                                        <label
                                            class="form-label fw-bold"
                                                for="automixPreset">{m.audio_description_export_automix_preset_label()}</label>
                                        <select
                                            class="form-select"
                                            bind:value={autoMixPreset}
                                            disabled={exporting}>
                                                {#each autoMixPresets as preset (preset.displayName ?? preset.type)}
                                                <option value={preset}
                                                        >{preset.type === "none" ? m.none_option() : preset.displayName} {preset.type === "fixedGain" ? m.audio_description_export_automix_fixed_gain_suffix() : ""}</option>
                                            {/each}
                                        </select>
                                        <div class="form-text">
                                            {#if autoMixPreset.type === "none"}
                                                    {m.audio_description_export_automix_none_help()}
                                            {:else if autoMixPreset.type === "fixedGain"}
                                                    {m.audio_description_export_automix_fixed_gain_help()}
                                            {:else}
                                                    {m.audio_description_export_automix_compression_help()}
                                            {/if}
                                        </div>
                                    </div>
                                    {#if autoMixPreset.displayName === "Custom" && autoMixPreset.type === "compression"}
                                        <div transition:fade={{ duration: 200 }}>
                                            <div class="row mb-3">
                                                <div class="col-md-6">
                                                    <label
                                                        class="form-label"
                                                            for="ratioInput">{m.audio_description_export_automix_ratio_label()}</label>
                                                    <input
                                                        type="number"
                                                        value={autoMixPreset.ratio}
                                                        onchange={(e) => {
                                                            autoMixPreset.ratio =
                                                                e.target.value;
                                                            autoMixPreset =
                                                                autoMixPreset;
                                                        }}
                                                        class="form-control"
                                                        min="1"
                                                        max="20"
                                                        step="0.01"
                                                        id="ratioInput"
                                                        disabled={exporting} />
                                                    <small class="form-text text-muted">
                                                        {m.audio_description_export_automix_ratio_help({ ratio: autoMixPreset.ratio })}
                                                    </small>
                                                </div>
                                                <div class="col-md-6">
                                                    <label
                                                        class="form-label"
                                                        for="thresholdInput"
                                                        >{m.audio_description_export_automix_threshold_label({ db: thresholdDb })}</label>
                                                    <input
                                                        type="number"
                                                        value={autoMixPreset.threshold}
                                                        onchange={(e) => {
                                                            autoMixPreset.threshold =
                                                                e.target.value;
                                                            autoMixPreset =
                                                                autoMixPreset;
                                                        }}
                                                        class="form-control"
                                                        min="0.00098000"
                                                        max="0.12"
                                                        step="0.001"
                                                        id="thresholdInput"
                                                        disabled={exporting} />
                                                    <small class="form-text text-muted">
                                                        {m.audio_description_export_automix_threshold_help()}
                                                    </small>
                                                </div>
                                            </div>
                                            <div class="row mb-3">
                                                <div class="col-md-6">
                                                    <label
                                                        class="form-label"
                                                        for="releaseInput"
                                                        >{m.audio_description_export_automix_release_label()}</label>
                                                    <input
                                                        type="number"
                                                        value={autoMixPreset.release}
                                                        onchange={(e) => {
                                                            autoMixPreset.release =
                                                                e.target.value;
                                                            autoMixPreset =
                                                                autoMixPreset;
                                                        }}
                                                        class="form-control"
                                                        min="1"
                                                        max="3000"
                                                        step="1"
                                                        id="releaseInput"
                                                        disabled={exporting} />
                                                    <small class="form-text text-muted">
                                                        {m.audio_description_export_automix_release_help()}
                                                    </small>
                                                </div>
                                                <div class="col-md-6">
                                                    <label
                                                        class="form-label"
                                                        for="attackInput">{m.audio_description_export_automix_attack_label()}</label>
                                                    <input
                                                        type="number"
                                                        value={autoMixPreset.attack}
                                                        onchange={(e) => {
                                                            autoMixPreset.attack =
                                                                e.target.value;
                                                            autoMixPreset =
                                                                autoMixPreset;
                                                        }}
                                                        class="form-control"
                                                        min="1"
                                                        max="3000"
                                                        step="1"
                                                        id="attackInput"
                                                        disabled={exporting} />
                                                    <small class="form-text text-muted">
                                                        {m.audio_description_export_automix_attack_help()}
                                                    </small>
                                                </div>
                                            </div>
                                            <div class="row mb-3">
                                                <div class="col-md-6">
                                                    <label
                                                        class="form-label"
                                                        for="kneeInput">{m.audio_description_export_automix_knee_label()}</label>
                                                    <input
                                                        type="number"
                                                        value={autoMixPreset.knee}
                                                        onchange={(e) => {
                                                            autoMixPreset.knee =
                                                                e.target.value;
                                                            autoMixPreset =
                                                                autoMixPreset;
                                                        }}
                                                        class="form-control"
                                                        min="1"
                                                        max="8"
                                                        step="0.01"
                                                        id="kneeInput"
                                                        disabled={exporting} />
                                                    <small class="form-text text-muted">
                                                        {m.audio_description_export_automix_knee_help()}
                                                    </small>
                                                </div>
                                                <div class="col-md-6">
                                                    <label
                                                        class="form-label"
                                                        for="mixInput">{m.audio_description_export_automix_mix_label()}</label>
                                                    <input
                                                        type="number"
                                                        value={autoMixPreset.mix}
                                                        onchange={(e) => {
                                                            autoMixPreset.mix =
                                                                e.target.value;
                                                            autoMixPreset =
                                                                autoMixPreset;
                                                        }}
                                                        class="form-control"
                                                        min="0"
                                                        max="1"
                                                        step="0.01"
                                                        id="mixInput"
                                                        disabled={exporting} />
                                                    <small class="form-text text-muted">
                                                        {m.audio_description_export_automix_mix_help()}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    {/if}
                                    <hr class="my-4" />
                                    <h6 class="mb-3"><i class="bi bi-volume-up"></i> {m.audio_description_export_loudness_title()}</h6>
                                    <div class="mb-3">
                                        <label
                                            class="form-label"
                                            for="loudnessProfile"
                                            >{m.audio_description_export_loudness_profile_label()}</label>
                                        <select
                                            class="form-select"
                                            bind:value={loudnessPreset}
                                            disabled={exporting}>
                                            {#each loudnessPresets as preset (preset.displayName ?? preset.target)}
                                                <option value={preset}
                                                    >{preset.target == null ? m.none_option() : preset.displayName}</option>
                                            {/each}
                                        </select>
                                        {#if !loudnessEnabled}
                                            <div class="form-text">
                                                {m.audio_description_export_loudness_none_help()}
                                            </div>
                                        {/if}
                                    </div>
                                    <div class="row">
                                        <div class="col-3">
                                            <div class="form-check form-switch">
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="voiceOverCheck"
                                                    bind:checked={loudnessVo} disabled={exporting || !loudnessEnabled} />
                                                <label
                                                    class="form-check-label"
                                                    for="voiceOverCheck"
                                                    >{m.audio_description_export_loudness_voice_over()}</label>
                                            </div>
                                        </div>
                                        <div class="col-3">
                                            <div class="form-check form-switch">
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="programCheck"
                                                    bind:checked={
                                                        loudnessProgram
                                                    } disabled={exporting || !loudnessEnabled}/>
                                                <label
                                                    class="form-check-label"
                                                    for="programCheck"
                                                    >{m.audio_description_export_loudness_program_audio()}</label>
                                            </div>
                                        </div>
                                        <div class="col-3">
                                            <div class="form-check form-switch">
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="mixdownCheck"
                                                    bind:checked={
                                                        loudnessMixdown
                                                    } disabled={exporting || !loudnessEnabled} />
                                                <label
                                                    class="form-check-label"
                                                    for="mixdownCheck"
                                                    >{m.audio_description_export_loudness_mixdown()}</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            {/if}
                        </div>
                    {/if}

                    <!-- Advanced Options Card -->
                    <div class="card mb-3">
                        <div class="card-header bg-light" role="button" tabindex="0" onclick={() => showAdvancedOptions = !showAdvancedOptions} onkeydown={(e) => e.key === 'Enter' && (showAdvancedOptions = !showAdvancedOptions)}>
                            <div class="d-flex justify-content-between align-items-center">
                                <h5 class="mb-0">
                                    <i class="bi bi-gear"></i> {m.audio_description_export_advanced_options_title()} <span class="badge bg-secondary">{m.audio_description_export_advanced_badge()}</span>
                                </h5>
                                <i class="bi bi-chevron-{showAdvancedOptions ? 'up' : 'down'}"></i>
                            </div>
                        </div>
                        {#if showAdvancedOptions}
                            <div class="card-body" transition:fade={{ duration: 200 }}>
                                <div class="row g-3">
                                    <div class="col-12">
                                        <div class="form-check form-switch">
                                            <input
                                                class="form-check-input"
                                                type="checkbox"
                                                id="selectionToggle"
                                                bind:checked={selection} 
                                                disabled={exporting || exportExtendedAD} />
                                            <label
                                                title={exportExtendedAD ? m.audio_description_export_selection_disabled_title() : ""}
                                                class="form-check-label fw-bold"
                                                for="selectionToggle">
                                                <i class="bi bi-list-check"></i> {m.audio_description_export_selection_label()}
                                            </label>
                                        </div>
                                    </div>
                                    {#if selection && !exportExtendedAD}
                                        <div class="col-md-6" transition:fade={{ duration: 200 }}>
                                            <label for="numericInput1" class="form-label">{m.audio_description_export_selection_first_label()}</label>
                                            <input
                                                type="number"
                                                class="form-control"
                                                id="numericInput1"
                                                bind:value={selectionStart}
                                                min="1"
                                                step="1" />
                                        </div>
                                        <div class="col-md-6" transition:fade={{ duration: 200 }}>
                                            <label for="numericInput2" class="form-label">{m.audio_description_export_selection_last_label()}</label>
                                            <input
                                                type="number"
                                                class="form-control"
                                                id="numericInput2"
                                                bind:value={selectionEnd}
                                                min="1"
                                                step="1" />
                                        </div>
                                    {/if}
                                    {#if !isDubbing}
                                    <div class="col-12">
                                        <div class="form-check form-switch">
                                            <input
                                                class="form-check-input"
                                                type="checkbox"
                                                id="exportExtendedADToggle"
                                                bind:checked={exportExtendedAD}
                                                disabled={exporting} />
                                            <label
                                                class="form-check-label fw-bold"
                                                for="exportExtendedADToggle">
                                                <i class="bi bi-pause-circle"></i> {m.audio_description_export_extended_ad_label()}
                                            </label>
                                        </div>
                                        {#if exportExtendedAD}
                                            <div class="alert alert-info mt-2 mb-0" transition:fade={{ duration: 200 }}>
                                                <small>
                                                    <i class="bi bi-info-circle"></i> {m.audio_description_export_extended_ad_help()}
                                                </small>
                                            </div>
                                        {/if}
                                    </div>
                                    {/if}
                                </div>
                            </div>
                        {/if}
                    </div>

                    <!-- Metadata Card (BWF for WAV files) -->
                    {#if audioPreset && audioPreset.format === "wav"}
                        <div class="card mb-3" transition:fade={{ duration: 200 }}>
                            <div class="card-header bg-light" role="button" tabindex="0" onclick={() => showMetadata = !showMetadata} onkeydown={(e) => e.key === 'Enter' && (showMetadata = !showMetadata)}>
                                <div class="d-flex justify-content-between align-items-center">
                                    <h5 class="mb-0">
                                        <i class="bi bi-tag"></i> {m.audio_description_export_bwf_title()} <span class="badge bg-secondary">{m.audio_description_export_bwf_badge()}</span>
                                    </h5>
                                    <i class="bi bi-chevron-{showMetadata ? 'up' : 'down'}"></i>
                                </div>
                            </div>
                            {#if showMetadata}
                                <div class="card-body" transition:fade={{ duration: 200 }}>
                                    <div class="mb-3">
                                        <div class="form-check form-switch">
                                            <input
                                                class="form-check-input"
                                                type="checkbox"
                                                id="bwfEnable"
                                                bind:checked={
                                                    bwfMetadata.enable
                                                } 
                                                disabled={exporting} />
                                            <label
                                                class="form-check-label fw-bold"
                                                for="bwfEnable">{m.audio_description_export_bwf_enable_label()}</label>
                                        </div>
                                    </div>
                                    <div class="mb-3">
                                        <label class="form-label" for="bwfTitle"
                                            >{m.audio_description_export_bwf_title_label()}</label>
                                        <input
                                            type="text"
                                            class="form-control"
                                            id="bwfTitle"
                                            bind:value={bwfMetadata.title}
                                            disabled={!bwfMetadata.enable} />
                                    </div>
                                    <div class="mb-3">
                                        <label
                                            class="form-label"
                                            for="bwfDescription"
                                            >{m.audio_description_export_bwf_description_label()}</label>
                                        <textarea
                                            class="form-control"
                                            id="bwfDescription"
                                            bind:value={bwfMetadata.description}
                                            disabled={!bwfMetadata.enable}
                                        ></textarea>
                                    </div>
                                    <div class="mb-3">
                                        <label
                                            class="form-label"
                                            for="bwfReferenceTime"
                                            >{m.audio_description_export_bwf_reference_time_label()}</label>
                                        <input
                                            type="text"
                                            class="form-control"
                                            id="bwfReferenceTime"
                                            bind:value={
                                                bwfMetadata.referenceTime
                                            }
                                            disabled={!bwfMetadata.enable} />
                                    </div>
                                </div>
                            {/if}
                        </div>
                    {/if}
                </form>
            </div>
            <div class="modal-footer">
                {#if exporting}
                    <div class="w-100" transition:fade={{ duration: 200 }}>
                        <div class="progress" style="height: 30px;">
                            <div
                                class="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                                role="progressbar"
                                style="width: {progress}%;"
                                aria-valuenow={progress}
                                aria-valuemin="0"
                                aria-valuemax="100">
                                <span class="fw-bold">{m.audio_description_export_progress_label({ percent: progress })}</span>
                            </div>
                        </div>
                        <p class="text-muted mt-2 mb-0">
                            <i class="bi bi-info-circle"></i>
                            {statusMsg}
                        </p>
                    </div>
                {:else}
                    <button
                        class="btn btn-sm btn-outline-primary"
                        type="button"
                        onclick={showLoadPresetDialog}
                        disabled={exporting}
                        title={m.audio_description_export_preset_load_title_hint()}>
                        <i class="bi bi-folder-open" aria-hidden="true"></i> {m.audio_description_export_load_button()}
                    </button>
                    <button
                        class="btn btn-sm btn-outline-primary"
                        type="button"
                        onclick={saveAsPreset}
                        disabled={!eventGroupId || exporting}
                        title={m.audio_description_export_preset_save_title_hint()}>
                        <i class="bi bi-save" aria-hidden="true"></i> {m.audio_description_export_save_button()}
                    </button>
                    <div class="ms-auto"></div>
                    <button
                        type="button"
                        disabled={!targetFolderPath || exporting || !eventGroupId}
                        class="btn btn-primary btn-lg"
                        onclick={beginExport}
                        id="exportAudioButton">
                        <i class="bi bi-download"></i> {m.audio_description_export_button()}
                    </button>
                {/if}
            </div>
        </div>
    </div>
</div>
